import { useState, useEffect, useCallback, useRef } from "react";
import { useApi, useAuth } from "./AuthContext";
import {
  ClipboardList, ChevronLeft, ChevronRight, Plus, X, Check, Clock,
  Calendar, Trash2, Inbox, User, Upload, Camera, RefreshCw,
  CheckCircle, AlertTriangle, Info, Image as ImageIcon, DollarSign, Receipt, FileText, Send, MailCheck, MailX, Users, ArrowLeft, Download,
  Pin, GripVertical, Truck, MapPin, MoreVertical
} from "lucide-react";
import { toast } from "sonner";

// ====================== SCHEDULE TRACK HELPERS ======================
// Mirrors backend `_track_orders_query`: decides whether an order belongs
// to a given track without any extra network round-trip. Used client-side
// to slice the already-loaded `orders` state per track+day for rendering.
function orderMatchesTrack(o, t) {
  if (!o || !t) return false;
  if (t.assignee_type === "team") {
    return !!t.assignee_id && o.team_id === t.assignee_id;
  }
  // user-type
  if (o.team_id) return false;
  const users = Array.isArray(o.assigned_user_ids) ? o.assigned_user_ids : [];
  if (t.is_default) {
    // Pure safety-net (no assignee): every un-teamed order lands here so
    // pre-existing orphan tickets stay visible on day one — matches the
    // backend `_track_orders_query` shortcut for assignee_id == null.
    if (!t.assignee_id) return true;
    if (users.length === 0) return true;
    if (users.includes(t.assignee_id)) return true;
    return false;
  }
  return !!t.assignee_id && users.includes(t.assignee_id);
}

// ====================== EVIDENCE LIGHTBOX ======================
function EvidenceLightbox({ open, photos, photoUrls, index, zoom, onIndex, onZoom, onClose, onAssignFacade }) {
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape") onClose?.();
      else if (e.key === "ArrowLeft") onIndex?.(Math.max(0, index - 1));
      else if (e.key === "ArrowRight") onIndex?.(Math.min(photos.length - 1, index + 1));
      else if (e.key === "+" || e.key === "=") onZoom?.(Math.min(4, zoom + 0.25));
      else if (e.key === "-") onZoom?.(Math.max(1, zoom - 0.25));
      else if (e.key === "0") onZoom?.(1);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, index, zoom, photos.length, onClose, onIndex, onZoom]);

  if (!open || !photos[index]) return null;
  const current = photos[index];
  const url = photoUrls[current.id];
  const canPrev = index > 0;
  const canNext = index < photos.length - 1;

  return (
    <div
      data-testid="evidence-lightbox"
      className="fixed inset-0 z-[140] flex items-center justify-center bg-black/90 backdrop-blur-sm"
      onMouseDown={(e) => { if (e.target === e.currentTarget) onClose?.(); }}
    >
      {/* Top bar */}
      <div className="absolute top-0 left-0 right-0 px-6 py-4 flex items-center justify-between text-white pointer-events-none">
        <div className="flex items-center gap-3 pointer-events-auto">
          <span className="text-sm font-semibold">Photo {index + 1} of {photos.length}</span>
          {current.original_filename && <span className="text-xs text-white/60 hidden md:inline">· {current.original_filename}</span>}
        </div>
        <div className="flex items-center gap-2 pointer-events-auto">
          {onAssignFacade && (
            <button
              type="button"
              onClick={() => onAssignFacade(current.id)}
              className="hidden md:inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold bg-forest-600 hover:bg-forest-700 text-white"
              title="Set this photo as the master Facade"
              data-testid="lightbox-assign-facade"
            >
              <ImageIcon className="w-3.5 h-3.5" /> Set as Facade
            </button>
          )}
          <button
            type="button"
            onClick={() => {
              if (!url) return;
              const a = document.createElement("a");
              a.href = url;
              a.download = current.original_filename || `evidence-${current.id}.jpg`;
              document.body.appendChild(a);
              a.click();
              a.remove();
            }}
            disabled={!url}
            title="Download photo"
            data-testid="lightbox-download"
            className="hidden md:inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold bg-white/10 hover:bg-white/20 text-white disabled:opacity-30"
          >
            <Download className="w-3.5 h-3.5" /> Download
          </button>
          <button
            type="button"
            onClick={() => onZoom?.(Math.max(1, zoom - 0.25))}
            disabled={zoom <= 1}
            className="w-9 h-9 rounded-full bg-white/10 hover:bg-white/20 disabled:opacity-30 flex items-center justify-center"
            title="Zoom out (-)"
            data-testid="lightbox-zoom-out"
          >
            <span className="text-lg font-bold leading-none">−</span>
          </button>
          <span className="text-xs font-medium w-12 text-center tabular-nums">{Math.round(zoom * 100)}%</span>
          <button
            type="button"
            onClick={() => onZoom?.(Math.min(4, zoom + 0.25))}
            disabled={zoom >= 4}
            className="w-9 h-9 rounded-full bg-white/10 hover:bg-white/20 disabled:opacity-30 flex items-center justify-center"
            title="Zoom in (+)"
            data-testid="lightbox-zoom-in"
          >
            <span className="text-lg font-bold leading-none">+</span>
          </button>
          <button
            type="button"
            onClick={onClose}
            className="w-9 h-9 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center ml-2"
            title="Close (Esc)"
            data-testid="lightbox-close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
      </div>

      {/* Prev */}
      <button
        type="button"
        onClick={() => canPrev && onIndex?.(index - 1)}
        disabled={!canPrev}
        className="absolute left-4 md:left-8 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-white/10 hover:bg-white/20 disabled:opacity-20 disabled:cursor-not-allowed flex items-center justify-center text-white transition-colors"
        title="Previous (←)"
        data-testid="lightbox-prev"
      >
        <ChevronLeft className="w-7 h-7" />
      </button>

      {/* Image */}
      <div className="max-w-[90vw] max-h-[80vh] overflow-auto flex items-center justify-center">
        {url ? (
          <img
            src={url}
            alt={current.original_filename || "Evidence"}
            style={{ transform: `scale(${zoom})`, transformOrigin: "center", transition: "transform 0.15s ease" }}
            className="max-w-full max-h-[80vh] object-contain select-none"
            draggable={false}
          />
        ) : (
          <div className="text-white/60 text-sm">Image not loaded</div>
        )}
      </div>

      {/* Next */}
      <button
        type="button"
        onClick={() => canNext && onIndex?.(index + 1)}
        disabled={!canNext}
        className="absolute right-4 md:right-8 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-white/10 hover:bg-white/20 disabled:opacity-20 disabled:cursor-not-allowed flex items-center justify-center text-white transition-colors"
        title="Next (→)"
        data-testid="lightbox-next"
      >
        <ChevronRight className="w-7 h-7" />
      </button>

      {/* Filmstrip */}
      <div className="absolute bottom-0 left-0 right-0 px-4 pb-4 pointer-events-none">
        <div className="max-w-4xl mx-auto bg-black/40 rounded-xl p-2 flex gap-2 overflow-x-auto pointer-events-auto">
          {photos.map((p, i) => (
            <button
              type="button"
              key={p.id}
              onClick={() => { onIndex?.(i); onZoom?.(1); }}
              className={`w-14 h-14 flex-shrink-0 rounded-md overflow-hidden border-2 transition-all ${i === index ? "border-forest-500 ring-2 ring-forest-500/40" : "border-white/20 hover:border-white/60"}`}
              data-testid={`lightbox-thumb-${p.id}`}
            >
              {photoUrls[p.id] ? <img src={photoUrls[p.id]} alt="" className="w-full h-full object-cover" /> : <div className="w-full h-full bg-stone-700" />}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
import { formatTime12h } from "../utils/time";
import TimeInput12h from "./TimeInput12h";
import BackButton from "./BackButton";
import CurrencyInput from "./CurrencyInput";
import useAdminEvents from "./useAdminEvents";
import { formatUSD } from "../utils/currency";

const STATUS_COLORS = {
  backlog: "bg-stone-100 text-stone-600",
  scheduled: "bg-blue-50 text-blue-700",
  in_progress: "bg-amber-50 text-amber-700",
  completed: "bg-green-50 text-green-700",
  cancelled: "bg-red-50 text-red-600",
};

/**
 * Helpers to parse ISO datetimes into HH:mm + date parts for the time picker,
 * and recombine the user's edits back into a full ISO timestamp.
 */
function isoToTimeHHMM(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    return `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
  } catch { return ""; }
}
function isoToDateLocal(iso) {
  if (!iso) return new Date();
  try { return new Date(iso); } catch { return new Date(); }
}
function combineDateAndTime(referenceIso, hhmm) {
  if (!hhmm) return null;
  const base = referenceIso ? isoToDateLocal(referenceIso) : new Date();
  const [hh, mm] = hhmm.split(":").map(Number);
  const out = new Date(base);
  out.setHours(hh || 0, mm || 0, 0, 0);
  return out.toISOString();
}
function fmtDateTimeLabel(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit", hour12: true });
  } catch { return iso; }
}

function ActualTimeBlock({ order, actualStart, actualEnd, onChangeStart, onChangeEnd }) {
  // HH:mm displayed in the picker, derived from the full ISO timestamp
  const startHHMM = isoToTimeHHMM(actualStart);
  const endHHMM = isoToTimeHHMM(actualEnd);

  // Reset start/end to the immutable raw mobile stamps
  const restoreRaw = () => {
    if (order?.raw_mobile_start) onChangeStart(order.raw_mobile_start);
    if (order?.raw_mobile_end) onChangeEnd(order.raw_mobile_end);
  };

  // Live duration badge
  let durationLabel = "Duration: —";
  if (actualStart && actualEnd) {
    const mins = Math.round((new Date(actualEnd).getTime() - new Date(actualStart).getTime()) / 60000);
    if (mins > 0) {
      const h = Math.floor(mins / 60); const m = mins % 60;
      durationLabel = `⏱️ Duration: ${h > 0 ? `${h}h ` : ""}${m}m`;
    }
  }

  // Latest manual-edit audit entry (rendered as the little log note)
  const audit = (order?.history_entries || [])
    .filter(e => e.type === "actual_time_edit")
    .slice(-1)[0];

  return (
    <div className="bg-emerald-50/40 border border-emerald-200 rounded-xl p-3 space-y-2" data-testid="actual-time-block">
      <div className="flex items-center justify-between">
        <p className="text-[11px] font-semibold uppercase tracking-wider text-emerald-700 flex items-center gap-1">
          <CheckCircle className="w-3 h-3" /> Actual Time (Field)
        </p>
        {(order?.raw_mobile_start || order?.raw_mobile_end) && (
          <button
            type="button"
            onClick={restoreRaw}
            data-testid="actual-time-restore"
            className="text-[10px] text-emerald-700 hover:text-emerald-900 underline-offset-2 hover:underline"
            title="Restore the original field-app stamps"
          >
            Restore app stamps
          </button>
        )}
      </div>
      <div className="grid grid-cols-2 gap-2">
        <div>
          <label className="block text-xs font-medium text-stone-600 mb-1">Start</label>
          <TimeInput12h
            value={startHHMM}
            onChange={(v) => onChangeStart(combineDateAndTime(actualStart || order?.scheduled_date || new Date().toISOString(), v))}
            testId="so-actual-start"
            disabled={!order}
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-stone-600 mb-1">End</label>
          <TimeInput12h
            value={endHHMM}
            onChange={(v) => onChangeEnd(combineDateAndTime(actualEnd || actualStart || order?.scheduled_date || new Date().toISOString(), v))}
            testId="so-actual-end"
            disabled={!order}
          />
        </div>
      </div>
      <p className="text-[11px] font-medium text-emerald-700">{durationLabel}</p>
      {audit && (
        <div className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-2 py-1.5 leading-snug" data-testid="actual-time-audit">
          📝 <strong>Note:</strong> Actual times edited manually by <strong>@{audit.created_by || "—"}</strong> on{" "}
          {fmtDateTimeLabel(audit.created_at)}.
          {audit.raw_mobile && (
            <span className="text-stone-500">{" "}(Original app stamp: {fmtDateTimeLabel(audit.raw_mobile)})</span>
          )}
        </div>
      )}
    </div>
  );
}

function formatElapsed(startIso) {
  if (!startIso) return "";
  const ms = Date.now() - new Date(startIso).getTime();
  if (Number.isNaN(ms) || ms < 0) return "";
  const total = Math.floor(ms / 1000);
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
}

/**
 * Time-Efficiency Bar
 *
 * Compares estimated duration (end_time − start_time, in minutes) against
 * actual duration (actual_end_time − actual_start_time, in minutes).
 *
 * - GREEN  : actual ≤ estimated → width proportional (actual/estimated %, min 8%)
 * - YELLOW : delay 1–10 min     → width 100% (color tells the story)
 * - RED    : delay > 10 min     → width 100%
 *
 * Returns null if the SO is not completed or required times are missing.
 */
function getEfficiencyBar(order) {
  if (!order || order.status !== "completed") return null;
  if (!order.start_time || !order.end_time) return null;
  if (!order.actual_start_time || !order.actual_end_time) return null;

  // Estimated duration: hh:mm − hh:mm (same day)
  const [eh1, em1] = (order.start_time || "00:00").split(":").map(Number);
  const [eh2, em2] = (order.end_time || "00:00").split(":").map(Number);
  const estimatedMin = (eh2 * 60 + em2) - (eh1 * 60 + em1);
  if (!estimatedMin || estimatedMin <= 0) return null;

  // Actual duration from ISO timestamps
  const startMs = new Date(order.actual_start_time).getTime();
  const endMs = new Date(order.actual_end_time).getTime();
  if (Number.isNaN(startMs) || Number.isNaN(endMs) || endMs <= startMs) return null;
  const actualMin = Math.round((endMs - startMs) / 60000);

  const delayMin = actualMin - estimatedMin;
  if (delayMin <= 0) {
    // On time / early — proportional width (min 8% so the bar stays visible)
    const pct = Math.max(8, Math.min(100, Math.round((actualMin / estimatedMin) * 100)));
    return { color: "bg-emerald-500", widthPct: pct, label: `On time · ${actualMin}/${estimatedMin}min`, severity: "green" };
  }
  if (delayMin <= 10) {
    return { color: "bg-amber-400", widthPct: 100, label: `+${delayMin}min over (${actualMin}/${estimatedMin}min)`, severity: "yellow" };
  }
  return { color: "bg-red-500", widthPct: 100, label: `+${delayMin}min over · critical (${actualMin}/${estimatedMin}min)`, severity: "red" };
}

export default function ServiceOrdersModule({ readOnly = false, onRequestInvoice, onViewInvoice, deepLinkOrderId = null, onDeepLinkConsumed, onBackToReferrer = null, referrerLabel = null, openNewOnMount = false, openBacklogOnMount = false, prefillClientId = null, onOpenNewConsumed }) {
  const api = useApi();
  const { hasPermission } = useAuth();
  const canSeeFinancial = hasPermission("financial", "view");
  const [weekStart, setWeekStart] = useState(() => {
    const d = new Date(); d.setDate(d.getDate() - d.getDay() + 1);
    return d;
  });
  const [orders, setOrders] = useState([]);
  const [backlogOrders, setBacklogOrders] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingOrder, setEditingOrder] = useState(null);
  const [showBacklog, setShowBacklog] = useState(true);
  const [scheduleBacklogId, setScheduleBacklogId] = useState(null);
  const [scheduleDate, setScheduleDate] = useState("");
  const [confirmModal, setConfirmModal] = useState(null);
  const [cascadeModal, setCascadeModal] = useState(null); // {mode:'update'|'delete', futureOpenCount, ...}
  const [completeModal, setCompleteModal] = useState(null);
  const [completePhotos, setCompletePhotos] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [renewModal, setRenewModal] = useState(null);
  const [invoicePromptModal, setInvoicePromptModal] = useState(null);
  const [photoUrls, setPhotoUrls] = useState({});
  const [facadeUrl, setFacadeUrl] = useState(null);
  // For CREATE mode: stage the file locally so the admin can upload it BEFORE the
  // service order exists. After Create succeeds we POST it to the new SO.
  const [pendingFacadeFile, setPendingFacadeFile] = useState(null);
  const [pendingFacadeUrl, setPendingFacadeUrl] = useState(null);
  const [dragOver, setDragOver] = useState(false);
  // True when this editor was opened via deep-link from another module.
  // On close (save/cancel/X) we return the user to the referrer tab.
  const [cameFromDeepLink, setCameFromDeepLink] = useState(false);

  const stageFacadeFile = (file) => {
    if (!file) return;
    if (!file.type?.startsWith("image/")) { toast.error("Please drop an image file"); return; }
    if (pendingFacadeUrl) URL.revokeObjectURL(pendingFacadeUrl);
    setPendingFacadeFile(file);
    setPendingFacadeUrl(URL.createObjectURL(file));
  };

  // Lazy-load the editor's facade photo as a blob URL
  useEffect(() => {
    setFacadeUrl(null);
    const path = editingOrder?.facade_photo?.storage_path;
    if (!path) return;
    let cancelled = false;
    (async () => {
      try {
        const resp = await api.get(`/admin/files/${path}`, { responseType: "blob" });
        if (!cancelled) setFacadeUrl(URL.createObjectURL(resp.data));
      } catch (err) { console.warn("[SO] failed to load facade:", err?.message || err); }
    })();
    return () => { cancelled = true; };
  }, [editingOrder?.id, editingOrder?.facade_photo?.storage_path, api]);

  const handleUploadFacade = async (file) => {
    if (!file || !editingOrder?.id) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post(`/admin/service-orders/${editingOrder.id}/facade`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setEditingOrder({ ...editingOrder, facade_photo: data });
      toast.success("Facade photo updated");
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || "Upload failed"); }
  };

  const handleRemoveFacade = async () => {
    if (!editingOrder?.id) return;
    if (!window.confirm("Remove the facade photo?")) return;
    try {
      await api.delete(`/admin/service-orders/${editingOrder.id}/facade`);
      setEditingOrder({ ...editingOrder, facade_photo: null });
      setFacadeUrl(null);
      toast.success("Facade removed");
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };

  // Assign one of the existing evidence photos as the facade (drag-and-drop or click).
  const handleAssignFacadeFromEvidence = async (photoId) => {
    if (!editingOrder?.id || !photoId) return;
    try {
      const { data } = await api.post(`/admin/service-orders/${editingOrder.id}/facade/from-photo/${photoId}`);
      setEditingOrder({ ...editingOrder, facade_photo: data });
      toast.success("Facade photo assigned from evidence");
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to assign facade"); }
  };

  // Lightbox / drag state for evidence photos
  const [lightboxIndex, setLightboxIndex] = useState(null);
  const [lightboxZoom, setLightboxZoom] = useState(1);
  const [draggingEvidenceId, setDraggingEvidenceId] = useState(null);
  const [facadeDropActive, setFacadeDropActive] = useState(false);
  const [draggingOrder, setDraggingOrder] = useState(null);
  const [dragOverDay, setDragOverDay] = useState(null);
  const [dragOverOrderId, setDragOverOrderId] = useState(null);
  const [jobExpenses, setJobExpenses] = useState([]);
  const [newJobExp, setNewJobExp] = useState({ description: "", amount: 0, vendor: "", payment_method: "" });
  const [loadingExpenses, setLoadingExpenses] = useState(false);

  const formatDate = (d) => {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${y}-${m}-${day}`;
  };

  const [form, setForm] = useState({
    client_id: "", scheduled_date: formatDate(new Date()),
    start_time: "08:00", end_time: "12:00", notes: "",
    services: [{ description: "", quantity: 1, unit_price: 0 }],
    recurrence: "one_time", recurrence_months: 3,
    assigned_user_ids: [],
  });
  const [teamMembers, setTeamMembers] = useState([]);
  // Service catalog — used to translate a client's `service_ids` into the
  // ticket's `services` rows (description + unit_price). Loaded once on mount.
  const [catalog, setCatalog] = useState([]);
  // Active teams list — powers the "Show only crew" filter chips on the main
  // weekly calendar so a manager can zoom into a single crew's day-by-day
  // schedule without leaving the primary view.
  const [teams, setTeams] = useState([]);
  // Schedule Tracks — stackable weekly calendars, one per Employee or Team.
  // Replaces the old single global calendar. `tracks` is fetched from
  // /api/admin/schedule-tracks and personalized per admin user.
  const [tracks, setTracks] = useState([]);
  const [tracksLoading, setTracksLoading] = useState(true);
  const [showNewTrackModal, setShowNewTrackModal] = useState(false);
  // Analysis view + missing clients (Fase 2 · Parts B, C)
  const [viewMode, setViewMode] = useState("calendar"); // "calendar" | "analysis"
  const [analysisGroupBy, setAnalysisGroupBy] = useState("city"); // "city" | "recurrence" | "value"
  const [analysisData, setAnalysisData] = useState(null);
  const [analysisLoading, setAnalysisLoading] = useState(false);
  const [showMissing, setShowMissing] = useState(false);
  const [missingClients, setMissingClients] = useState([]);
  const [missingLoading, setMissingLoading] = useState(false);
  // Part D · Next-slot cache per (track_id, dayStr). Populated by fetchNextSlots
  // whenever the visible week or tracks change so the "+" button label can
  // preview the next available start time (e.g. "+ 13:30").
  const [nextSlots, setNextSlots] = useState({}); // {"trackId::dayStr": "HH:MM"}
  // Fase 3 · Part 5 — payment confirmation modal state.
  const [paymentModalOrder, setPaymentModalOrder] = useState(null);
  const [showBillingLegend, setShowBillingLegend] = useState(false);
  const fetchTracks = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/schedule-tracks");
      setTracks(Array.isArray(data) ? data : []);
    } catch (err) {
      console.warn("[SO] tracks fetch:", err?.message);
    } finally {
      setTracksLoading(false);
    }
  }, [api]);
  useEffect(() => { fetchTracks(); }, [fetchTracks]);

  // Fetch day_completed_totals and per-order billing_status per track for
  // the current week. Both are financial-gated server-side (field simply
  // absent for users without permission). Merged into `tracks` (totals) and
  // `orders` (billing_status per order.id).
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const ws = formatDate(weekStart);
        const { data } = await api.get(`/admin/schedule/tracks-week?start=${ws}`);
        if (cancelled) return;
        const totalsById = {};
        const billingByOrderId = {};
        (data?.tracks || []).forEach(t => {
          if (t.day_completed_totals) totalsById[t.id] = t.day_completed_totals;
          Object.values(t.days || {}).forEach(cards => {
            cards.forEach(c => {
              if (c.billing_status) billingByOrderId[c.id] = c.billing_status;
            });
          });
        });
        setTracks(prev => prev.map(t =>
          totalsById[t.id] ? { ...t, day_completed_totals: totalsById[t.id] } : t
        ));
        if (Object.keys(billingByOrderId).length) {
          setOrders(prev => prev.map(o =>
            billingByOrderId[o.id] ? { ...o, billing_status: billingByOrderId[o.id] } : o
          ));
        }
      } catch (_err) { /* silent — enrichments are optional UI */ }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [weekStart, orders.length, tracks.length]);

  // Load team members once for assignment dropdown
  useEffect(() => {
    api.get("/admin/users").then(r => setTeamMembers(r.data || [])).catch(() => {});
    api.get("/admin/service-catalog").then(r => setCatalog(Array.isArray(r.data) ? r.data : [])).catch(() => {});
    api.get("/admin/workforce/teams").then(r => setTeams(Array.isArray(r.data) ? r.data : [])).catch(() => {});
  }, [api]);

  // Build the `form.services` rows from a client's contracted services.
  // Returns an empty array when nothing is contracted — callers decide what
  // to do (typically keep the manual row).
  const buildServicesFromClient = useCallback((client) => {
    if (!client) return [];
    const catById = Object.fromEntries(catalog.map(c => [c.id, c]));
    const customPrices = (client.service_prices && typeof client.service_prices === "object")
      ? client.service_prices : {};
    const rows = (client.service_ids || [])
      .map(id => {
        const item = catById[id];
        if (!item) return null;
        const price = customPrices[id] !== undefined
          ? Number(customPrices[id])
          : Number(item.default_price) || 0;
        return { description: item.name, quantity: 1, unit_price: price };
      })
      .filter(Boolean);
    if (client.services_other && client.services_other.trim()) {
      rows.push({ description: client.services_other.trim(), quantity: 1, unit_price: 0 });
    }
    return rows;
  }, [catalog]);

  // True if the current services array is "blank" (one untouched default row
  // or nothing at all) — safe to overwrite from the client's contracted list.
  const isServicesBlank = (services) => {
    if (!Array.isArray(services) || services.length === 0) return true;
    if (services.length > 1) return false;
    const s = services[0];
    return !s.description && (!s.unit_price || Number(s.unit_price) === 0);
  };

  const handleClientChange = (newClientId) => {
    setForm(prev => {
      const next = { ...prev, client_id: newClientId };
      if (!newClientId) return next;
      // Only auto-fill when the user hasn't started entering services. This
      // protects existing edits, while accelerating brand-new tickets — the
      // manager picks a client and the contracted services snap in.
      if (isServicesBlank(prev.services)) {
        const client = clients.find(c => c.id === newClientId);
        const auto = buildServicesFromClient(client);
        if (auto.length) next.services = auto;
      }
      return next;
    });
  };

  const fetchData = useCallback(async () => {
    try {
      const ws = formatDate(weekStart);
      const [ordersRes, backlogRes] = await Promise.all([
        api.get(`/admin/service-orders?week_start=${ws}`),
        api.get("/admin/service-orders?backlog=true"),
      ]);
      setOrders(ordersRes.data);
      setBacklogOrders(backlogRes.data);
    } catch (err) {
      toast.error("Failed to load service orders");
    } finally {
      setLoading(false);
    }
  }, [api, weekStart]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // 1-second ticker for elapsed time display on in_progress cards (only mounts when needed)
  const hasInProgress = orders.some((o) => o.status === "in_progress" && o.actual_start_time);
  const [nowTick, setNowTick] = useState(0);
  useEffect(() => {
    if (!hasInProgress) return;
    const id = setInterval(() => setNowTick((n) => n + 1), 1000);
    return () => clearInterval(id);
  }, [hasInProgress]);

  // Live updates from field operators (Server-Sent Events)
  useAdminEvents((ev) => {
    if (!ev || !ev.type) return;
    if (ev.type === "so_started") {
      toast.success(`${ev.user_name || "Field operator"} started a job`, { duration: 3500 });
      fetchData();
      if (editingOrder?.id === ev.service_order_id) {
        setEditingOrder((prev) => prev ? { ...prev, status: "in_progress", actual_start_time: ev.actual_start_time } : prev);
      }
    } else if (ev.type === "so_finished") {
      toast.success(`${ev.user_name || "Field operator"} finished a job`, { duration: 3500 });
      fetchData();
      if (editingOrder?.id === ev.service_order_id) {
        setEditingOrder((prev) => prev ? { ...prev, status: "completed", actual_end_time: ev.actual_end_time } : prev);
      }
    } else if (ev.type === "so_photo_added") {
      // Silent refresh when the editor is open on this SO
      if (editingOrder?.id === ev.service_order_id) {
        toast.success(`New photo uploaded by ${ev.user_name || "field"}`, { duration: 2500 });
        fetchData();
      }
    }
  }, { enabled: !readOnly });

  useEffect(() => {
    (async () => {
      try { const { data } = await api.get("/admin/clients"); setClients(data); }
      catch (err) { console.warn("Failed to load clients:", err?.message); }
    })();
  }, [api]);

  const getWeekDays = () => {
    const days = [];
    for (let i = 0; i < 7; i++) {
      const d = new Date(weekStart);
      d.setDate(d.getDate() + i);
      days.push(d);
    }
    return days;
  };

  const navigateWeek = (dir) => {
    const d = new Date(weekStart);
    d.setDate(d.getDate() + dir * 7);
    setWeekStart(d);
  };

  const resetForm = () => {
    const wasDeepLink = cameFromDeepLink;
    setForm({ client_id: "", scheduled_date: formatDate(new Date()), start_time: "08:00", end_time: "12:00", notes: "", services: [{ description: "", quantity: 1, unit_price: 0 }], recurrence: "one_time", recurrence_months: 3, assigned_user_ids: [] });
    setEditingOrder(null); setShowForm(false);
    setJobExpenses([]); setNewJobExp({ description: "", amount: 0, vendor: "", payment_method: "" });
    if (pendingFacadeUrl) URL.revokeObjectURL(pendingFacadeUrl);
    setPendingFacadeFile(null); setPendingFacadeUrl(null);
    if (wasDeepLink) {
      setCameFromDeepLink(false);
      onBackToReferrer?.();
    }
  };

  // Auto-fill start_time/end_time and preassign the team/user from the
  // clicked track. Falls back to 08:00–12:00 if the endpoint is offline.
  const openNewForm = async (date, trackId = null) => {
    resetForm();
    let start = "08:00";
    let end = "12:00";
    const patch = { scheduled_date: date };
    if (trackId) {
      try {
        const { data } = await api.get(`/admin/service-orders/next-slot?track_id=${trackId}&date=${date}`);
        if (data?.start_time) start = data.start_time;
        if (data?.end_time) end = data.end_time;
      } catch (_err) { /* keep defaults */ }
      const tr = tracks.find(t => t.id === trackId);
      if (tr) {
        if (tr.assignee_type === "team" && tr.assignee_id) {
          patch.team_id = tr.assignee_id;
          const team = teams.find(x => x.id === tr.assignee_id);
          patch.assigned_user_ids = (team?.member_user_ids || []).map(String);
        } else if (tr.assignee_type === "user" && tr.assignee_id) {
          patch.team_id = null;
          patch.assigned_user_ids = [tr.assignee_id];
        }
      }
    }
    setForm(f => ({ ...f, ...patch, start_time: start, end_time: end }));
    setShowForm(true);
  };

  const addService = () => setForm(f => ({ ...f, services: [...f.services, { description: "", quantity: 1, unit_price: 0 }] }));
  const removeService = (i) => setForm(f => ({ ...f, services: f.services.filter((_, idx) => idx !== i) }));
  const updateService = (i, field, val) => {
    setForm(f => {
      const svc = [...f.services];
      svc[i] = { ...svc[i], [field]: field === "description" ? val : Number(val) };
      return { ...f, services: svc };
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const doSave = async (mode = "single") => {
      try {
        if (editingOrder) {
          if (mode === "series") {
            const { data } = await api.put(`/admin/service-orders/${editingOrder.id}/cascade-update`, form);
            toast.success(`Updated ${data.affected_count} ticket${data.affected_count === 1 ? "" : "s"} in the series`);
          } else {
            await api.put(`/admin/service-orders/${editingOrder.id}`, form);
            toast.success("Service order updated");
          }
        } else {
          const { data } = await api.post("/admin/service-orders", form);
          if (pendingFacadeFile) {
            const targetIds = data?.ids?.length ? data.ids : (data?.id ? [data.id] : []);
            for (const sid of targetIds) {
              try {
                const fd = new FormData();
                fd.append("file", pendingFacadeFile);
                await api.post(`/admin/service-orders/${sid}/facade`, fd, { headers: { "Content-Type": "multipart/form-data" } });
              } catch (err) {
                toast.error(`Ticket saved but facade upload failed for ${sid}`);
              }
            }
          }
          if (data?.count > 1) toast.success(`${data.count} recurring service orders created!`);
          else toast.success("Service order created");
        }
        resetForm(); fetchData();
      } catch (err) { toast.error(err.response?.data?.detail || "Failed to save"); }
    };

    // If editing a recurring SO AND user changed price-impacting fields → ask
    const isRecurring = editingOrder?.recurrence_group_id && editingOrder?.recurrence && editingOrder.recurrence !== "one_time";
    const priceChanged = (() => {
      if (!editingOrder) return false;
      const oldSvcs = editingOrder.services || [];
      const newSvcs = form.services || [];
      if (oldSvcs.length !== newSvcs.length) return true;
      for (let i = 0; i < newSvcs.length; i++) {
        const a = oldSvcs[i] || {};
        const b = newSvcs[i] || {};
        if ((a.description || "") !== (b.description || "")) return true;
        if (Number(a.quantity || 0) !== Number(b.quantity || 0)) return true;
        if (Number(a.unit_price || 0) !== Number(b.unit_price || 0)) return true;
      }
      return false;
    })();

    if (editingOrder?.status === "completed") {
      setConfirmModal({
        title: "Edit completed service?",
        message: "This job is already completed. Changes will be logged to the Activity Trail and Client History. Save anyway?",
        onConfirm: async () => { setConfirmModal(null); await doSave("single"); },
      });
      return;
    }

    if (isRecurring && priceChanged) {
      try {
        const { data } = await api.get(`/admin/service-orders/${editingOrder.id}/series-info`);
        const futureOpen = data?.future_open_count || 0;
        if (futureOpen > 0) {
          setCascadeModal({
            mode: "update",
            futureOpenCount: futureOpen,
            onSingle: async () => { setCascadeModal(null); await doSave("single"); },
            onSeries: async () => { setCascadeModal(null); await doSave("series"); },
          });
          return;
        }
      } catch (err) { console.warn("series-info failed:", err?.message); }
    }
    await doSave("single");
  };

  // Complete Job flow with centered modal
  const openCompleteModal = (order) => {
    setCompleteModal(order);
    setCompletePhotos([]);
    // Load existing photos
    if (order.photos?.length) {
      const urls = {};
      order.photos.forEach(async (p) => {
        try {
          const resp = await api.get(`/admin/files/${p.storage_path}`, { responseType: "blob" });
          urls[p.id] = URL.createObjectURL(resp.data);
          setPhotoUrls(prev => ({ ...prev, ...urls }));
        } catch (err) { console.warn("[SO] failed to load photo blob:", err?.message || err); }
      });
    }
  };

  const handleUploadJobPhoto = async (files) => {
    if (!completeModal || !files?.length) return;
    setUploading(true);
    try {
      for (const file of files) {
        const fd = new FormData();
        fd.append("file", file);
        await api.post(`/admin/service-orders/${completeModal.id}/photos`, fd, {
          headers: { "Content-Type": "multipart/form-data" }
        });
      }
      toast.success(`${files.length} photo(s) uploaded`);
      const { data } = await api.get(`/admin/service-orders/${completeModal.id}`);
      setCompleteModal(data);
      // Reload photo URLs
      if (data.photos?.length) {
        for (const p of data.photos) {
          if (!photoUrls[p.id]) {
            try {
              const resp = await api.get(`/admin/files/${p.storage_path}`, { responseType: "blob" });
              setPhotoUrls(prev => ({ ...prev, [p.id]: URL.createObjectURL(resp.data) }));
            } catch (err) { /* */ }
          }
        }
      }
    } catch (err) { toast.error("Upload failed"); }
    finally { setUploading(false); }
  };

  const buildInvoicePrefill = (so) => {
    const items = (so.services || [])
      .filter(s => s && (s.description || s.unit_price))
      .map(s => ({
        description: s.description || "",
        quantity: Number(s.quantity || 1),
        unit_price: Number(s.unit_price || 0),
      }));
    if (!items.length) {
      items.push({ description: "Service rendered", quantity: 1, unit_price: Number(so.total_amount || 0) });
    }
    return {
      service_order_id: so.id,
      client_id: so.client_id,
      client_name: so.client_name,
      items,
      notes: so.notes ? `Service Order #${(so.id || "").slice(0, 8)} — ${so.notes}` : `Service Order #${(so.id || "").slice(0, 8)}`,
      total_amount: Number(so.total_amount || items.reduce((a, b) => a + (b.quantity * b.unit_price), 0)),
    };
  };

  const handleCompleteJob = async () => {
    if (!completeModal) return;
    try {
      const { data } = await api.post(`/admin/service-orders/${completeModal.id}/complete-job`);
      toast.success("Job completed!");
      const completedSO = { ...completeModal };
      const renewPayload = (data.is_last_in_group && data.recurrence && data.recurrence !== "one_time") ? {
        recurrence: data.recurrence,
        recurrence_months: data.recurrence_months,
        recurrence_group_id: data.recurrence_group_id,
        client_id: completeModal.client_id,
        client_name: completeModal.client_name,
        services: completeModal.services,
        start_time: completeModal.start_time,
        end_time: completeModal.end_time,
        notes: completeModal.notes,
      } : null;
      setCompleteModal(null);
      setPhotoUrls({});
      fetchData();
      // Show invoice prompt FIRST. Pending renew (if any) fires after the prompt is dismissed.
      setInvoicePromptModal({ so: completedSO, renewPayload });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to complete job");
    }
  };

  const [sendingConfirmation, setSendingConfirmation] = useState(false);

  const handleSendConfirmation = async () => {
    if (!editingOrder?.id) return;
    if (!window.confirm("Send the appointment confirmation email to the client?")) return;
    setSendingConfirmation(true);
    try {
      const { data } = await api.post(`/admin/service-orders/${editingOrder.id}/send-confirmation`);
      toast.success("Confirmation email queued for the client.");
      // Refresh editingOrder + lists so the pill updates
      setEditingOrder({
        ...editingOrder,
        confirmation_status: "waiting",
        confirmation_token: data.token,
        confirmation_sent_at: new Date().toISOString(),
      });
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to send confirmation");
    } finally {
      setSendingConfirmation(false);
    }
  };

  const handleRenew = async () => {
    if (!renewModal) return;
    try {
      const lastSO = await api.get("/admin/service-orders?status=completed");
      const groupOrders = (lastSO.data || []).filter(o => o.recurrence_group_id === renewModal.recurrence_group_id);
      const lastDate = groupOrders.length ? groupOrders.sort((a, b) => b.scheduled_date?.localeCompare(a.scheduled_date))[0]?.scheduled_date : formatDate(new Date());
      const nextDate = new Date(lastDate);
      if (renewModal.recurrence === "biweekly") nextDate.setDate(nextDate.getDate() + 14);
      else nextDate.setMonth(nextDate.getMonth() + 1);

      await api.post("/admin/service-orders", {
        client_id: renewModal.client_id,
        scheduled_date: formatDate(nextDate),
        start_time: renewModal.start_time || "08:00",
        end_time: renewModal.end_time || "12:00",
        services: renewModal.services || [{ description: "Service", quantity: 1, unit_price: 0 }],
        notes: renewModal.notes || "",
        recurrence: renewModal.recurrence,
        recurrence_months: renewModal.recurrence_months,
      });
      toast.success("Recurring schedule renewed!");
      setRenewModal(null);
      fetchData();
    } catch (err) {
      toast.error("Failed to renew");
    }
  };

  const handleDelete = async (order) => {
    const isRecurring = order?.recurrence_group_id && order?.recurrence && order.recurrence !== "one_time";
    const doSingleDelete = async () => {
      try { await api.delete(`/admin/service-orders/${order.id}`); toast.success("Order deleted"); fetchData(); }
      catch (err) { toast.error("Failed to delete order"); }
    };
    const doCascadeDelete = async () => {
      try {
        const { data } = await api.delete(`/admin/service-orders/${order.id}/cascade-delete`);
        toast.success(`Deleted ${data.deleted_count} ticket${data.deleted_count === 1 ? "" : "s"} from the series`);
        fetchData();
      } catch (err) { toast.error(err.response?.data?.detail || "Failed to delete series"); }
    };

    if (!isRecurring) {
      setConfirmModal({
        title: "Delete this service order?",
        message: `"${order.client_name}" on ${order.scheduled_date || "backlog"} will be removed.`,
        danger: true,
        onConfirm: async () => { setConfirmModal(null); await doSingleDelete(); },
      });
      return;
    }
    // Recurring: ask user — fetch series info first to surface count
    try {
      const { data } = await api.get(`/admin/service-orders/${order.id}/series-info`);
      const futureOpen = data?.future_open_count || 0;
      setCascadeModal({
        mode: "delete",
        futureOpenCount: futureOpen,
        clientName: order.client_name,
        scheduledDate: order.scheduled_date,
        onSingle: async () => { setCascadeModal(null); await doSingleDelete(); },
        onSeries: async () => { setCascadeModal(null); await doCascadeDelete(); },
      });
    } catch (err) {
      // Fallback to plain confirm
      setConfirmModal({
        title: "Delete this service order?",
        message: `"${order.client_name}" on ${order.scheduled_date || "backlog"} will be removed.`,
        danger: true,
        onConfirm: async () => { setConfirmModal(null); await doSingleDelete(); },
      });
    }
  };

  const handleScheduleBacklog = async (orderId) => {
    if (!scheduleDate) { toast.error("Select a date"); return; }
    try {
      await api.put(`/admin/service-orders/${orderId}`, { scheduled_date: scheduleDate, status: "scheduled" });
      toast.success("Moved to schedule!");
      setScheduleBacklogId(null);
      setScheduleDate("");
      fetchData();
    } catch (err) { toast.error("Failed to schedule"); }
  };

  const handleEdit = (order) => {
    setEditingOrder(order);
    setForm({
      client_id: order.client_id, scheduled_date: order.scheduled_date || formatDate(new Date()),
      start_time: order.start_time || "08:00", end_time: order.end_time || "12:00",
      notes: order.notes || "",
      services: order.services?.length > 0 ? order.services : [{ description: "", quantity: 1, unit_price: 0 }],
      recurrence: "one_time", recurrence_months: 3,
      assigned_user_ids: order.assigned_user_ids || [],
      actual_start_time: order.actual_start_time || "",
      actual_end_time: order.actual_end_time || "",
    });
    setShowForm(true);
    fetchJobExpenses(order.id);
    // Load photos
    if (order.photos?.length) {
      order.photos.forEach(async (p) => {
        if (p.storage_path && !photoUrls[p.id]) {
          try {
            const resp = await api.get(`/admin/files/${p.storage_path}`, { responseType: "blob" });
            setPhotoUrls(prev => ({ ...prev, [p.id]: URL.createObjectURL(resp.data) }));
          } catch (err) { console.warn("[SO] failed to load editor photo:", err?.message || err); }
        }
      });
    }
  };

  // Home shortcut → "New Service Order" / "Backlog". Open the new-ticket form
  // (or just ensure backlog tray is visible) once on mount, then ack the parent.
  // When opened via the Client Details "Create New Ticket" shortcut, `prefillClientId`
  // pre-selects the client and auto-populates the Services list from the client's
  // contracted services (catalog + free-text "Others"), so the manager just confirms
  // the schedule.
  const homeIntentRef = useRef(false);
  useEffect(() => {
    if (homeIntentRef.current) return;
    if (openNewOnMount) {
      homeIntentRef.current = true;
      setShowForm(true);
      if (prefillClientId) {
        // Best-effort enrichment — failures fall back to just selecting the client.
        (async () => {
          try {
            const [clientRes, catalogRes] = await Promise.all([
              api.get(`/admin/clients/${prefillClientId}`),
              api.get("/admin/service-catalog"),
            ]);
            const client = clientRes.data || {};
            const cat = Array.isArray(catalogRes.data) ? catalogRes.data : [];
            // Keep the local catalog cache fresh in case the user picks a
            // different client from the dropdown afterwards.
            setCatalog(cat);
            const catById = Object.fromEntries(cat.map(c => [c.id, c]));
            const customPrices = (client.service_prices && typeof client.service_prices === "object")
              ? client.service_prices : {};
            const services = (client.service_ids || [])
              .map(id => {
                const item = catById[id];
                if (!item) return null;
                // Per-client override takes precedence over catalog default
                const price = customPrices[id] !== undefined
                  ? Number(customPrices[id])
                  : Number(item.default_price) || 0;
                return {
                  description: item.name,
                  quantity: 1,
                  unit_price: price,
                };
              })
              .filter(Boolean);
            if (client.services_other && client.services_other.trim()) {
              services.push({ description: client.services_other.trim(), quantity: 1, unit_price: 0 });
            }
            setForm(f => ({
              ...f,
              client_id: prefillClientId,
              services: services.length ? services : f.services,
            }));
          } catch (err) {
            // Network/permission issue — still set the client so the manager
            // doesn't have to re-pick it.
            setForm(f => ({ ...f, client_id: prefillClientId }));
          }
        })();
      }
      onOpenNewConsumed?.();
    } else if (openBacklogOnMount) {
      homeIntentRef.current = true;
      setShowBacklog(true);
      // Scroll the backlog into view shortly after mount
      setTimeout(() => {
        const el = document.querySelector('[data-testid="backlog-area"]');
        if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 350);
      onOpenNewConsumed?.();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openNewOnMount, openBacklogOnMount, prefillClientId]);

  // Deep-link: if parent passes an order ID, fetch + open the editor automatically.
  // We track the last handled ID in a ref so React 18 StrictMode's double-mount
  // doesn't cancel the edit before the modal opens. The parent clears the prop
  // (`onDeepLinkConsumed`) only after we've successfully populated the form.
  const handledDeepLinkRef = useRef(null);
  useEffect(() => {
    if (!deepLinkOrderId) return;
    if (handledDeepLinkRef.current === deepLinkOrderId) return; // already in-flight / done
    handledDeepLinkRef.current = deepLinkOrderId;
    (async () => {
      try {
        const { data } = await api.get(`/admin/service-orders/${deepLinkOrderId}`);
        if (!data) throw new Error("empty");
        setCameFromDeepLink(true);
        handleEdit(data);
      } catch (err) {
        console.warn("[SO deep-link] fetch failed:", err?.response?.status, err?.message);
        toast.error("Could not open that ticket — it may have been deleted.");
        onBackToReferrer?.();
      } finally {
        onDeepLinkConsumed?.();
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deepLinkOrderId]);

  const fetchJobExpenses = async (orderId) => {
    setLoadingExpenses(true);
    try {
      const { data } = await api.get(`/admin/service-orders/${orderId}/expenses`);
      setJobExpenses(data);
    } catch (err) { console.warn("[SO] fetchJobExpenses failed:", err?.message || err); setJobExpenses([]); }
    finally { setLoadingExpenses(false); }
  };

  const handleAddJobExpense = async () => {
    if (!editingOrder || !newJobExp.description.trim() || newJobExp.amount <= 0) {
      toast.error("Description and valid amount required"); return;
    }
    try {
      await api.post(`/admin/service-orders/${editingOrder.id}/expenses`, newJobExp);
      toast.success("Job expense added");
      setNewJobExp({ description: "", amount: 0, vendor: "", payment_method: "" });
      fetchJobExpenses(editingOrder.id);
      fetchData(); // Refresh SO to update job_expenses_total
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };

  const handleUploadJobExpReceipt = async (expenseId, file) => {
    if (!editingOrder) return;
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post(`/admin/service-orders/${editingOrder.id}/expenses/${expenseId}/receipt`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Receipt uploaded");
      fetchJobExpenses(editingOrder.id);
    } catch (err) { toast.error("Upload failed"); }
  };

  const handleUploadEvidence = async (files) => {
    if (!editingOrder || !files?.length) return;
    for (const file of files) {
      try {
        const fd = new FormData();
        fd.append("file", file);
        await api.post(`/admin/service-orders/${editingOrder.id}/photos`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      } catch (err) { toast.error("Upload failed"); }
    }
    toast.success(`${files.length} photo(s) uploaded`);
    // Refresh the editing order
    try {
      const { data } = await api.get(`/admin/service-orders/${editingOrder.id}`);
      setEditingOrder(data);
      if (data.photos?.length) {
        for (const p of data.photos) {
          if (p.storage_path && !photoUrls[p.id]) {
            try {
              const resp = await api.get(`/admin/files/${p.storage_path}`, { responseType: "blob" });
              setPhotoUrls(prev => ({ ...prev, [p.id]: URL.createObjectURL(resp.data) }));
            } catch (err) { console.warn("[SO] failed to load evidence photo:", err?.message || err); }
          }
        }
      }
    } catch (err) { console.warn("[SO] failed to refresh after evidence upload:", err?.message || err); }
  };

  // ===== Drag and Drop Handlers =====
  const handleDragStart = (e, order) => {
    if (readOnly || order.status === "completed" || order.status === "cancelled") {
      e.preventDefault();
      return;
    }
    setDraggingOrder(order);
    e.dataTransfer.effectAllowed = "move";
    e.dataTransfer.setData("text/plain", order.id);
  };

  const handleDragEnd = () => {
    setDraggingOrder(null);
    setDragOverDay(null);
    setDragOverOrderId(null);
  };

  const handleDragOver = (e, dayStr) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    if (dragOverDay !== dayStr) setDragOverDay(dayStr);
  };

  const handleDragLeave = () => {
    setDragOverDay(null);
  };

  const persistRouteOrder = async (dayStr, ids) => {
    try {
      await api.put("/admin/service-orders/reorder", { scheduled_date: dayStr, order: ids });
    } catch (err) {
      toast.error("Failed to save route order");
      fetchData();
    }
  };

  const handleCardDragOver = (e, orderId) => {
    e.preventDefault();
    e.stopPropagation();
    e.dataTransfer.dropEffect = "move";
    if (dragOverOrderId !== orderId) setDragOverOrderId(orderId);
  };

  const handleCardDrop = async (e, targetOrder) => {
    e.preventDefault();
    e.stopPropagation();
    const source = draggingOrder;
    setDraggingOrder(null);
    setDragOverDay(null);
    setDragOverOrderId(null);
    if (!source || source.id === targetOrder.id) return;

    const sourceDate = source.scheduled_date;
    const targetDate = targetOrder.scheduled_date;

    // Cross-day drop: first move to target day, then place before target in that day's route
    if (sourceDate !== targetDate) {
      try {
        await api.put(`/admin/service-orders/${source.id}`, { scheduled_date: targetDate });
      } catch (_err) {
        toast.error("Failed to reschedule");
        return;
      }
    }

    // Build new ordered list for target day by reading current state + moving source before target
    const dayList = orders
      .filter(o => (o.id === source.id ? false : o.scheduled_date === targetDate))
      .sort(routeSort);
    const targetIdx = dayList.findIndex(o => o.id === targetOrder.id);
    const insertAt = targetIdx < 0 ? dayList.length : targetIdx;
    const movedSource = { ...source, scheduled_date: targetDate };
    const nextList = [...dayList.slice(0, insertAt), movedSource, ...dayList.slice(insertAt)];

    // Optimistic UI update
    setOrders(prev => prev.map(o => {
      if (o.scheduled_date !== targetDate && o.id !== source.id) return o;
      const idx = nextList.findIndex(x => x.id === o.id);
      if (idx === -1) return o;
      return { ...o, scheduled_date: targetDate, route_order: idx + 1 };
    }));

    await persistRouteOrder(targetDate, nextList.map(o => o.id));
    if (sourceDate !== targetDate) {
      toast.success(`Moved & reordered (${targetDate})`);
    }
  };

  // Convert a track into the extra fields we need to patch onto a dropped
  // ticket so it "belongs" to that track. Never touches other fields.
  const dropFieldsForTrack = (track) => {
    if (!track) return {};
    if (track.assignee_type === "team") {
      if (!track.assignee_id) return {};
      const team = teams.find(x => x.id === track.assignee_id);
      return {
        team_id: track.assignee_id,
        assigned_user_ids: (team?.member_user_ids || []).map(String),
      };
    }
    // user-type
    const out = { team_id: null };
    if (track.assignee_id) out.assigned_user_ids = [track.assignee_id];
    return out;
  };

  const handleDrop = async (e, targetDayStr, targetTrack = null) => {
    e.preventDefault();
    setDragOverDay(null);
    setDragOverOrderId(null);
    const order = draggingOrder;
    setDraggingOrder(null);
    if (!order) return;
    const trackFields = dropFieldsForTrack(targetTrack);
    const needsReschedule = order.scheduled_date !== targetDayStr;
    const needsTrackChange = Object.keys(trackFields).length > 0 && (
      trackFields.team_id !== order.team_id ||
      JSON.stringify(trackFields.assigned_user_ids || []) !== JSON.stringify(order.assigned_user_ids || [])
    );
    if (needsReschedule || needsTrackChange) {
      try {
        await api.put(`/admin/service-orders/${order.id}`, { scheduled_date: targetDayStr, ...trackFields });
      } catch (_err) {
        toast.error("Failed to reschedule");
        return;
      }
    }
    // Route order — only inside the target day (client-side ordering unchanged)
    const dayList = orders
      .filter(o => o.id !== order.id && o.scheduled_date === targetDayStr)
      .sort(routeSort);
    const nextIds = [...dayList.map(o => o.id), order.id];
    setOrders(prev => prev.map(o => {
      if (o.id === order.id) return { ...o, scheduled_date: targetDayStr, route_order: nextIds.length, ...trackFields };
      if (o.scheduled_date !== targetDayStr) return o;
      const idx = nextIds.indexOf(o.id);
      return idx >= 0 ? { ...o, route_order: idx + 1 } : o;
    }));
    await persistRouteOrder(targetDayStr, nextIds);
  };

  // Sort helper: route_order ascending, fallback to start_time/created_at
  const routeSort = (a, b) => {
    const ra = a.route_order ?? 999;
    const rb = b.route_order ?? 999;
    if (ra !== rb) return ra - rb;
    const ta = a.start_time || "";
    const tb = b.start_time || "";
    if (ta !== tb) return ta.localeCompare(tb);
    return (a.created_at || "").localeCompare(b.created_at || "");
  };

  const weekDays = getWeekDays();
  const wsLabel = weekStart.toLocaleDateString("en-US", { month: "short", day: "numeric" });
  const weEnd = new Date(weekStart); weEnd.setDate(weEnd.getDate() + 6);
  const weLabel = weEnd.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });


  // Analysis fetcher — runs on demand when the tab or group-by changes
  const fetchAnalysis = useCallback(async () => {
    if (viewMode !== "analysis") return;
    setAnalysisLoading(true);
    setAnalysisData(null); // clear stale shape (city groups vs value items)
    try {
      const ws = formatDate(weekStart);
      const { data } = await api.get(`/admin/schedule/analysis?start=${ws}&group_by=${analysisGroupBy}`);
      setAnalysisData(data);
    } catch (err) {
      toast.error("Falha ao carregar análise");
      setAnalysisData(null);
    } finally {
      setAnalysisLoading(false);
    }
  }, [api, viewMode, analysisGroupBy, weekStart]);
  useEffect(() => { fetchAnalysis(); }, [fetchAnalysis]);

  // Missing-clients fetcher — runs when the mask is toggled on or week moves
  const fetchMissing = useCallback(async () => {
    if (!showMissing) return;
    setMissingLoading(true);
    try {
      const ws = formatDate(weekStart);
      const { data } = await api.get(`/admin/schedule/missing-clients?start=${ws}`);
      setMissingClients(data?.missing || []);
    } catch (err) {
      setMissingClients([]);
    } finally {
      setMissingLoading(false);
    }
  }, [api, showMissing, weekStart]);
  useEffect(() => { fetchMissing(); }, [fetchMissing]);

  // Batch-fetch next-slot for every visible (track, day) pair so the "+"
  // button in each empty cell can preview the time. One call per cell — the
  // volume is small (~14 for the default + 1 track over 7 days). Any single
  // failure is silent so ticket creation is never blocked.
  useEffect(() => {
    if (tracks.length === 0) return;
    let cancelled = false;
    (async () => {
      const results = {};
      await Promise.all(tracks.flatMap(t =>
        weekDays.map(async (day) => {
          const dayStr = formatDate(day);
          try {
            const { data } = await api.get(`/admin/service-orders/next-slot?track_id=${t.id}&date=${dayStr}`);
            if (data?.start_time) results[`${t.id}::${dayStr}`] = data.start_time;
          } catch (_err) { /* fallback: plain "+" */ }
        })
      ));
      if (!cancelled) setNextSlots(results);
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tracks, weekStart, orders.length]);

  // "Agendar" from missing-client card — opens the new-ticket flow pre-filled.
  const handleScheduleMissing = (mc) => {
    resetForm();
    const nextMonday = new Date(weekStart);
    setForm(f => ({
      ...f,
      client_id: mc.client_id,
      client_name: mc.client_name,
      scheduled_date: formatDate(nextMonday),
      start_time: "08:00",
      end_time: "09:00",
    }));
    setShowForm(true);
  };

  // Fase 3 · Part 5 — confirm payment.
  // Handles both branches: (a) ticket already has an invoice → PUT
  // /invoices/{id} status=paid + method/note; (b) ticket has no invoice
  // yet ("not_created") → POST /invoices with paid_in_advance=true first,
  // then optionally PUT the just-created invoice to record method/note.
  const handleConfirmPayment = async (order, method, note) => {
    if (!order) return;
    try {
      let invoiceId = order.invoice_id;
      if (!invoiceId) {
        // Auto-create a fresh invoice from the ticket in a single call.
        const items = (order.services || []).map(s => ({
          description: s.description || "",
          quantity: s.quantity || 1,
          unit_price: s.price || s.unit_price || 0,
          total: (s.quantity || 1) * (s.price || s.unit_price || 0),
        }));
        const total = order.total_amount || items.reduce((a, b) => a + (b.total || 0), 0);
        const { data: inv } = await api.post("/admin/invoices", {
          client_id: order.client_id,
          service_order_id: order.id,
          items,
          total_amount: total,
          paid_in_advance: true,
          notes: note || "",
        });
        invoiceId = inv.id;
      }
      // Second call records method + note into history and ensures paid state.
      await api.put(`/admin/invoices/${invoiceId}`, {
        status: "paid",
        payment_method: method,
        payment_note: note || null,
      });
      toast.success("Pagamento confirmado");
      setPaymentModalOrder(null);
      // Optimistic: mark card paid immediately so the border turns green
      // without waiting for the SSE tick.
      setOrders(prev => prev.map(o =>
        o.id === order.id ? { ...o, billing_status: "paid", invoice_id: invoiceId } : o
      ));
    } catch (err) {
      toast.error(err.response?.data?.detail || "Falha ao confirmar pagamento");
    }
  };


  // ---------- Schedule Track handlers (pin, reorder, delete, create) ----------
  const [draggingTrack, setDraggingTrack] = useState(null);
  const togglePin = async (track) => {
    const nextPinned = !track.is_pinned;
    // Optimistic
    setTracks(prev => prev.map(t => t.id === track.id ? { ...t, is_pinned: nextPinned } : t)
                          .sort((a, b) => (a.is_pinned ? 0 : 1) - (b.is_pinned ? 0 : 1)));
    try {
      await api.put(`/admin/schedule-tracks/${track.id}/preference`, { is_pinned: nextPinned });
    } catch (err) {
      toast.error("Falha ao atualizar preferência");
      fetchTracks();
    }
  };
  // Organize the visible week for a track: loops through the 7 days,
  // calls /schedule/organize-day for each that has 2+ non-completed tickets,
  // then refetches. One button, one intent, no per-day chrome.
  const [organizingTrackId, setOrganizingTrackId] = useState(null);
  const handleOrganizeWeek = async (track) => {
    setOrganizingTrackId(track.id);
    try {
      const trackOrders = orders.filter(o => orderMatchesTrack(o, track));
      const daysWithMultiple = new Set();
      trackOrders.forEach(o => {
        if (o.status !== "completed" && o.status !== "cancelled" && o.scheduled_date) {
          const sameDay = trackOrders.filter(x =>
            x.scheduled_date === o.scheduled_date && x.status !== "completed" && x.status !== "cancelled"
          );
          if (sameDay.length >= 2) daysWithMultiple.add(o.scheduled_date);
        }
      });
      const visibleDays = new Set(weekDays.map(d => formatDate(d)));
      const targets = [...daysWithMultiple].filter(d => visibleDays.has(d));
      if (targets.length === 0) {
        toast.info("Nada para organizar nesta semana (precisa 2+ tickets por dia)");
        return;
      }
      let total = 0;
      for (const date of targets) {
        try {
          const { data } = await api.post("/admin/schedule/organize-day", { track_id: track.id, date });
          total += data?.reordered || 0;
        } catch (_err) { /* keep going */ }
      }
      toast.success(`Reorganizado: ${total} ticket${total === 1 ? "" : "s"} em ${targets.length} dia${targets.length === 1 ? "" : "s"}`);
      await fetchData();
    } finally {
      setOrganizingTrackId(null);
    }
  };

  const handleDeleteTrack = async (track) => {
    if (track.is_default) return;
    setConfirmModal({
      title: "Excluir agenda?",
      message: `A agenda "${track.label}" será removida. Os tickets existentes permanecem intactos.`,
      danger: true,
      onConfirm: async () => {
        try {
          await api.delete(`/admin/schedule-tracks/${track.id}`);
          setConfirmModal(null);
          fetchTracks();
        } catch (err) {
          toast.error(err.response?.data?.detail || "Falha ao excluir agenda");
        }
      },
    });
  };
  const handleTrackDragStart = (e, track) => {
    setDraggingTrack(track);
    e.dataTransfer.effectAllowed = "move";
    // Custom mime so we can distinguish from order-card drags
    e.dataTransfer.setData("application/gow-track", track.id);
  };
  const handleTrackDragOver = (e) => {
    if (draggingTrack) { e.preventDefault(); e.dataTransfer.dropEffect = "move"; }
  };
  const handleTrackDrop = async (e, targetTrack) => {
    e.preventDefault();
    e.stopPropagation();
    const src = draggingTrack;
    setDraggingTrack(null);
    if (!src || src.id === targetTrack.id) return;
    const nextPinned = targetTrack.is_pinned;
    const list = [...tracks];
    const srcIdx = list.findIndex(t => t.id === src.id);
    const tgtIdx = list.findIndex(t => t.id === targetTrack.id);
    if (srcIdx < 0 || tgtIdx < 0) return;
    const [moved] = list.splice(srcIdx, 1);
    moved.is_pinned = nextPinned;
    const newTgtIdx = list.findIndex(t => t.id === targetTrack.id);
    list.splice(newTgtIdx, 0, moved);
    const pinned = list.filter(t => t.is_pinned);
    const unpinned = list.filter(t => !t.is_pinned);
    const updated = [...pinned, ...unpinned].map((t, i) => ({ ...t, personal_position: i }));
    setTracks(updated);
    // Persist EVERY affected track's position so the ordering survives
    // reload. Only writing the moved track leaves ties that the backend
    // sort resolves alphabetically, silently reverting the drag.
    try {
      await Promise.all(updated.map(t =>
        api.put(`/admin/schedule-tracks/${t.id}/preference`, {
          is_pinned: !!t.is_pinned,
          position: t.personal_position,
        })
      ));
    } catch (_err) {
      toast.error("Falha ao reordenar");
      fetchTracks();
    }
  };

  if (loading) return <div className="flex items-center justify-center h-64 text-stone-500">Loading...</div>;

  return (
    <div data-testid="service-orders-module" className="space-y-6">
      {/* Deep-link breadcrumb (shown only when arrived from another module) */}
      {onBackToReferrer && (
        <button
          onClick={onBackToReferrer}
          data-testid="so-back-to-referrer"
          className="inline-flex items-center gap-2 text-sm font-medium text-forest-700 bg-forest-50 hover:bg-forest-100 border border-forest-200 px-3 py-1.5 rounded-lg transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to {referrerLabel || "Financial"}
        </button>
      )}
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-forest-50 rounded-lg flex items-center justify-center">
            <ClipboardList className="w-5 h-5 text-forest-600" />
          </div>
          <div>
            <h2 className="text-lg font-semibold text-stone-800">Service Orders</h2>
            <p className="text-sm text-stone-500">{orders.length} scheduled · {backlogOrders.length} backlog</p>
          </div>
        </div>
        {!readOnly && <button data-testid="add-so-btn" onClick={() => openNewForm(formatDate(new Date()))} className="flex items-center gap-2 bg-forest-600 hover:bg-forest-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors">
          <Plus className="w-4 h-4" /> New Service Order
        </button>}
      </div>

      {/* Awaiting Release & Schedule (Backlog) */}
      {backlogOrders.length > 0 && (() => {
        // Sort: client_approval first (most recent first within group), then proposal_approval, then others
        const sortedBacklog = [...backlogOrders].sort((a, b) => {
          const sourceWeight = (s) => s === "client_approval" ? 0 : s === "proposal_approval" ? 1 : 2;
          const wa = sourceWeight(a.source);
          const wb = sourceWeight(b.source);
          if (wa !== wb) return wa - wb;
          return (b.created_at || "").localeCompare(a.created_at || "");
        });
        const clientApprovedCount = backlogOrders.filter(o => o.source === "client_approval").length;
        return (
          <div data-testid="backlog-area" className="bg-amber-50/50 border border-amber-200 rounded-xl overflow-hidden">
            <button data-testid="backlog-toggle" onClick={() => setShowBacklog(!showBacklog)}
              className="w-full px-5 py-3 flex items-center justify-between text-left hover:bg-amber-50 transition-colors">
              <div className="flex items-center gap-2 flex-wrap">
                <Inbox className="w-5 h-5 text-amber-600" />
                <span className="font-semibold text-stone-800">Awaiting Release &amp; Schedule</span>
                <span className="bg-amber-200 text-amber-800 text-xs font-bold px-2 py-0.5 rounded-full">{backlogOrders.length}</span>
                {clientApprovedCount > 0 && (
                  <span className="inline-flex items-center gap-1 bg-green-600 text-white text-xs font-bold px-2 py-0.5 rounded-full" data-testid="backlog-client-approved-count">
                    <CheckCircle className="w-3 h-3" /> {clientApprovedCount} new client {clientApprovedCount === 1 ? "approval" : "approvals"}
                  </span>
                )}
              </div>
              <ChevronRight className={`w-4 h-4 text-stone-400 transition-transform ${showBacklog ? "rotate-90" : ""}`} />
            </button>
            {showBacklog && (
              <div className="px-5 pb-4 space-y-2">
                {sortedBacklog.map(order => {
                  const isClientApproved = order.source === "client_approval";
                  const isProposalApproved = order.source === "proposal_approval";
                  return (
                    <div
                      key={order.id}
                      data-testid={`backlog-card-${order.id}`}
                      className={`rounded-lg px-4 py-3 flex items-center gap-4 border ${
                        isClientApproved ? "bg-white border-green-300 shadow-sm ring-1 ring-green-200" : "bg-white border-stone-200"
                      }`}
                    >
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                        isClientApproved ? "bg-green-100" : "bg-stone-100"
                      }`}>
                        {isClientApproved ? <CheckCircle className="w-4 h-4 text-green-600" /> : <User className="w-4 h-4 text-stone-500" />}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <div className="font-medium text-stone-800 text-sm">{order.client_name}</div>
                          {isClientApproved && (
                            <span className="inline-flex items-center gap-1 bg-green-50 text-green-700 border border-green-200 text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full">
                              <CheckCircle className="w-2.5 h-2.5" /> Approved by Client
                            </span>
                          )}
                          {isProposalApproved && !isClientApproved && (
                            <span className="inline-flex items-center gap-1 bg-blue-50 text-blue-700 border border-blue-200 text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full">
                              <FileText className="w-2.5 h-2.5" /> From Proposal
                            </span>
                          )}
                        </div>
                        <div className="text-xs text-stone-500 truncate">{order.services?.map(s => s.description).join(", ") || "No services"}</div>
                      </div>
                      {order.total_amount > 0 && <span className="text-sm font-semibold text-forest-700">{formatUSD(order.total_amount)}</span>}
                      {!readOnly && (
                        <>
                          {scheduleBacklogId === order.id ? (
                            <div className="flex items-center gap-2">
                              <input type="date" data-testid="backlog-date-input" value={scheduleDate} onChange={e => setScheduleDate(e.target.value)} className="px-2 py-1 border border-stone-300 rounded-lg text-sm" />
                              <button onClick={() => handleScheduleBacklog(order.id)} className="p-1.5 rounded-md bg-forest-600 text-white hover:bg-forest-700"><Check className="w-4 h-4" /></button>
                              <button onClick={() => { setScheduleBacklogId(null); setScheduleDate(""); }} className="p-1.5 rounded-md text-stone-400 hover:text-stone-600"><X className="w-4 h-4" /></button>
                            </div>
                          ) : (
                            <div className="flex items-center gap-1">
                              <button data-testid={`schedule-backlog-${order.id}`} onClick={() => { setScheduleBacklogId(order.id); setScheduleDate(formatDate(new Date())); }}
                                className={`flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-medium transition-colors ${
                                  isClientApproved
                                    ? "bg-forest-600 text-white hover:bg-forest-700"
                                    : "bg-forest-50 text-forest-700 hover:bg-forest-100"
                                }`}
                              >
                                <Calendar className="w-3.5 h-3.5" /> {isClientApproved ? "Release & Schedule" : "Schedule"}
                              </button>
                              <button onClick={() => handleDelete(order)} title="Delete" className="p-1 rounded-md text-stone-400 hover:text-red-600 hover:bg-red-50"><Trash2 className="w-3.5 h-3.5" /></button>
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        );
      })()}

      {/* Form */}
      {!readOnly && showForm && (
        <div className="bg-white rounded-xl border border-stone-200 p-6 shadow-sm" data-testid="so-form">
          <div className="flex items-center justify-between mb-1">
            <div className="flex items-center gap-3 flex-wrap">
              <h3 className="font-semibold text-stone-800">{editingOrder ? "Edit Service Order" : "New Service Order"}</h3>
            </div>
            <button onClick={resetForm} className="text-stone-400 hover:text-stone-600"><X className="w-5 h-5" /></button>
          </div>
          <div className="flex items-center gap-2 flex-wrap mt-2">
            <BackButton onClick={resetForm} testId="so-form-back" />
            {editingOrder && (
              editingOrder.invoice_id ? (
                <button
                  type="button"
                  data-testid="view-linked-invoice-btn"
                  onClick={() => onViewInvoice && onViewInvoice(editingOrder.invoice_id)}
                  className="inline-flex items-center gap-2 bg-blue-50 text-blue-700 hover:bg-blue-100 border border-blue-200 px-3 py-2 rounded-lg text-sm font-semibold transition-colors"
                >
                  <Receipt className="w-4 h-4" /> View Linked Invoice
                </button>
              ) : (
                <button
                  type="button"
                  data-testid="create-invoice-btn"
                  onClick={() => onRequestInvoice && onRequestInvoice(buildInvoicePrefill(editingOrder))}
                  className="inline-flex items-center gap-2 bg-forest-600 hover:bg-forest-700 text-white px-3 py-2 rounded-lg text-sm font-semibold shadow-sm transition-colors"
                  title="Open Financial module with this order's data prefilled"
                >
                  <DollarSign className="w-4 h-4" /> Create Invoice
                </button>
              )
            )}
            {editingOrder && editingOrder.status !== "completed" && editingOrder.status !== "cancelled" && (
              <>
                <button
                  type="button"
                  data-testid="send-confirmation-btn"
                  onClick={handleSendConfirmation}
                  disabled={sendingConfirmation}
                  className="inline-flex items-center gap-2 bg-amber-50 text-amber-800 hover:bg-amber-100 border border-amber-200 px-3 py-2 rounded-lg text-sm font-semibold transition-colors disabled:opacity-50"
                  title={editingOrder.confirmation_status ? "Resend appointment confirmation email" : "Send appointment confirmation email to the client"}
                >
                  <Send className="w-4 h-4" />
                  {editingOrder.confirmation_status === "waiting" ? "Resend Confirmation" :
                   editingOrder.confirmation_status === "confirmed" ? "Resend Confirmation" :
                   editingOrder.confirmation_status === "declined" ? "Send New Confirmation" :
                   "Send Confirmation Request"}
                </button>
                {editingOrder.confirmation_status === "waiting" && (
                  <span data-testid="confirmation-status-pill" className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold bg-amber-100 text-amber-800 border border-amber-200">
                    <Clock className="w-3 h-3" /> Waiting Client
                  </span>
                )}
                {editingOrder.confirmation_status === "confirmed" && (
                  <span data-testid="confirmation-status-pill" className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold bg-green-100 text-green-800 border border-green-200">
                    <MailCheck className="w-3 h-3" /> Confirmed
                  </span>
                )}
                {editingOrder.confirmation_status === "declined" && (
                  <span data-testid="confirmation-status-pill" className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold bg-red-100 text-red-800 border border-red-200">
                    <MailX className="w-3 h-3" /> Declined
                  </span>
                )}
              </>
            )}
          </div>
          <div className="mt-3" />
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-stone-700 mb-1">Client</label>
                <select data-testid="so-client" value={form.client_id} onChange={e => handleClientChange(e.target.value)}
                  className="w-full px-3 py-2 border border-stone-300 rounded-lg text-sm" required>
                  <option value="">Select client...</option>
                  {clients.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-stone-700 mb-1 flex items-center justify-between gap-2">
                  <span>Scheduled Date</span>
                  {editingOrder?.recurrence_group_id && editingOrder?.recurrence && editingOrder.recurrence !== "one_time" && (
                    <span
                      data-testid="so-recurrence-badge"
                      className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-cyan-50 text-cyan-700 border border-cyan-200"
                      title={`Part of a ${editingOrder.recurrence} series`}
                    >
                      <RefreshCw className="w-3 h-3" />
                      {editingOrder.recurrence === "weekly" ? "Weekly" : editingOrder.recurrence === "biweekly" ? "Bi-weekly" : editingOrder.recurrence === "monthly" ? "Monthly" : editingOrder.recurrence}
                      {editingOrder.recurrence_index != null && editingOrder.recurrence_total
                        ? ` · ${editingOrder.recurrence_index + 1}/${editingOrder.recurrence_total}`
                        : ""}
                    </span>
                  )}
                </label>
                <input data-testid="so-date" type="date" value={form.scheduled_date} onChange={e => setForm({ ...form, scheduled_date: e.target.value })}
                  className="w-full px-3 py-2 border border-stone-300 rounded-lg text-sm" required />
              </div>
            </div>

            {/* Estimated vs Actual Time */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Left column: Estimated */}
              <div className="bg-stone-50/50 border border-stone-200 rounded-xl p-3 space-y-2" data-testid="estimated-time-block">
                <p className="text-[11px] font-semibold uppercase tracking-wider text-stone-500 flex items-center gap-1">
                  <Clock className="w-3 h-3" /> Estimated Time
                </p>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <label className="block text-xs font-medium text-stone-600 mb-1">Start</label>
                    <TimeInput12h value={form.start_time} onChange={(v) => setForm({ ...form, start_time: v })} testId="so-start" />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-stone-600 mb-1">End</label>
                    <TimeInput12h value={form.end_time} onChange={(v) => setForm({ ...form, end_time: v })} testId="so-end" />
                  </div>
                </div>
                <p className="text-[11px] text-stone-400">{(() => {
                  const [sh, sm] = (form.start_time || "00:00").split(":").map(Number);
                  const [eh, em] = (form.end_time || "00:00").split(":").map(Number);
                  const mins = (eh * 60 + em) - (sh * 60 + sm);
                  if (mins <= 0) return "Duration: —";
                  const h = Math.floor(mins / 60); const m = mins % 60;
                  return `Duration: ${h > 0 ? `${h}h ` : ""}${m}m`;
                })()}</p>
              </div>

              {/* Right column: Actual (only when editing an existing SO) */}
              {editingOrder ? (
                <ActualTimeBlock
                  order={editingOrder}
                  actualStart={form.actual_start_time}
                  actualEnd={form.actual_end_time}
                  onChangeStart={(v) => setForm({ ...form, actual_start_time: v })}
                  onChangeEnd={(v) => setForm({ ...form, actual_end_time: v })}
                />
              ) : (
                <div className="bg-stone-50/30 border border-dashed border-stone-200 rounded-xl p-3 text-center text-xs text-stone-400 flex items-center justify-center" data-testid="actual-time-placeholder">
                  Actual times will appear here once the field crew clocks in.
                </div>
              )}
            </div>

            {/* Recurrence Section */}
            {!editingOrder && (
              <div className="p-4 bg-forest-50/50 rounded-xl border border-forest-100 space-y-3" data-testid="recurrence-section">
                <p className="text-sm font-semibold text-forest-800 flex items-center gap-2"><RefreshCw className="w-4 h-4" /> Recurrence</p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs font-medium text-stone-600 mb-1 block">Frequency</label>
                    <select data-testid="so-recurrence" value={form.recurrence} onChange={e => setForm({ ...form, recurrence: e.target.value })}
                      className="w-full px-3 py-2 border border-stone-300 rounded-lg text-sm bg-white">
                      <option value="one_time">One-time</option>
                      <option value="weekly">Weekly (Every week)</option>
                      <option value="biweekly">Bi-weekly (Every 2 weeks)</option>
                      <option value="monthly">Monthly</option>
                    </select>
                  </div>
                  {form.recurrence !== "one_time" && (
                    <div>
                      <label className="text-xs font-medium text-stone-600 mb-1 block">Duration</label>
                      <select data-testid="so-recurrence-months" value={form.recurrence_months} onChange={e => setForm({ ...form, recurrence_months: Number(e.target.value) })}
                        className="w-full px-3 py-2 border border-stone-300 rounded-lg text-sm bg-white">
                        <option value={3}>3 months</option>
                        <option value={6}>6 months</option>
                        <option value={9}>9 months</option>
                        <option value={12}>12 months</option>
                      </select>
                    </div>
                  )}
                </div>
                {form.recurrence !== "one_time" && (
                  <p className="text-xs text-forest-600">
                    This will auto-generate {form.recurrence === "weekly" ? "weekly" : form.recurrence === "biweekly" ? "bi-weekly" : "monthly"} service orders for {form.recurrence_months} months starting from the scheduled date.
                  </p>
                )}
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-stone-700 mb-1">Services</label>
              {form.services.map((svc, i) => (
                <div key={`svc-${i}`} className="flex gap-2 mb-2">
                  <input placeholder="Description" value={svc.description} onChange={e => updateService(i, "description", e.target.value)}
                    className="flex-1 px-3 py-2 border border-stone-300 rounded-lg text-sm" required />
                  <input type="number" placeholder="Qty" value={svc.quantity} onChange={e => updateService(i, "quantity", e.target.value)}
                    className="w-20 px-3 py-2 border border-stone-300 rounded-lg text-sm" min="1" />
                  <CurrencyInput value={svc.unit_price} onChange={(v) => updateService(i, "unit_price", v)}
                    className="w-28 px-3 py-2 border border-stone-300 rounded-lg text-sm" data-testid={`so-svc-price-${i}`} />
                  {form.services.length > 1 && (
                    <button type="button" onClick={() => removeService(i)} className="text-red-500 hover:text-red-700"><X className="w-4 h-4" /></button>
                  )}
                </div>
              ))}
              <button type="button" onClick={addService} className="text-forest-600 text-sm font-medium hover:text-forest-700 flex items-center gap-1">
                <Plus className="w-3 h-3" /> Add service
              </button>
            </div>
            <div>
              <label className="block text-sm font-medium text-stone-700 mb-1">Notes</label>
              <textarea value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} rows={2}
                className="w-full px-3 py-2 border border-stone-300 rounded-lg text-sm resize-none" />
            </div>

            {/* Assigned Workers + Facade Photo (admin-only references) */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div className="bg-stone-50 rounded-xl border border-stone-200 p-4 space-y-2" data-testid="assigned-workers-section">
                <p className="text-sm font-semibold text-stone-700 flex items-center gap-2"><Users className="w-4 h-4 text-forest-600" /> Assigned Workers</p>
                <p className="text-xs text-stone-500">Pick the team members who will see this ticket on the Field App.</p>
                <div className="space-y-1 max-h-40 overflow-y-auto pr-1">
                  {teamMembers.filter(u => ["field_operator","operador","operador_gerente","admin"].includes(u.role)).map(u => {
                    const checked = form.assigned_user_ids.includes(u._id);
                    return (
                      <label key={u._id} className="flex items-center gap-2 px-2 py-1.5 rounded-lg hover:bg-white cursor-pointer min-h-[36px]">
                        <input
                          type="checkbox"
                          data-testid={`assign-${u._id}`}
                          checked={checked}
                          onChange={e => {
                            setForm(f => ({
                              ...f,
                              assigned_user_ids: e.target.checked
                                ? [...f.assigned_user_ids, u._id]
                                : f.assigned_user_ids.filter(id => id !== u._id),
                            }));
                          }}
                          className="w-4 h-4 accent-forest-600"
                        />
                        <span className="text-sm text-stone-700 truncate">{u.name}</span>
                        <span className="ml-auto text-[10px] text-stone-400 capitalize">{u.role.replace("_", " ")}</span>
                      </label>
                    );
                  })}
                  {teamMembers.length === 0 && <p className="text-xs text-stone-400 py-2 text-center">No team members</p>}
                </div>
              </div>

              <div className="bg-stone-50 rounded-xl border border-stone-200 p-4 space-y-2" data-testid="facade-section">
                <div className="flex items-center justify-between">
                  <p className="text-sm font-semibold text-stone-700 flex items-center gap-2"><ImageIcon className="w-4 h-4 text-forest-600" /> Facade Photo</p>
                  {editingOrder?.facade_photo && (
                    <button type="button" onClick={handleRemoveFacade} className="text-xs text-red-600 hover:text-red-700 font-semibold" data-testid="facade-remove-btn">Remove</button>
                  )}
                </div>
                <p className="text-xs text-stone-500">Master reference shown to the field operator so they can confirm the correct property.</p>
                {!editingOrder ? (
                  pendingFacadeUrl ? (
                    <div className="space-y-2" data-testid="facade-pending-preview">
                      <img src={pendingFacadeUrl} alt="Facade (pending)" className="w-full h-32 object-cover rounded-lg" />
                      <p className="text-[11px] text-amber-700 font-semibold flex items-center gap-1">
                        <Upload className="w-3 h-3" /> Will be uploaded after the ticket is created
                      </p>
                      <div className="flex gap-2">
                        <label className="flex-1 text-center text-xs text-forest-700 hover:text-forest-800 font-semibold cursor-pointer border border-stone-300 rounded-lg py-2 min-h-[36px] inline-flex items-center justify-center" data-testid="facade-pending-replace-btn">
                          Replace
                          <input type="file" accept="image/*" className="hidden" onChange={(e) => stageFacadeFile(e.target.files?.[0])} />
                        </label>
                        <button
                          type="button"
                          data-testid="facade-pending-remove-btn"
                          onClick={() => {
                            if (pendingFacadeUrl) URL.revokeObjectURL(pendingFacadeUrl);
                            setPendingFacadeFile(null); setPendingFacadeUrl(null);
                          }}
                          className="flex-1 text-xs text-red-600 hover:text-red-700 font-semibold border border-stone-300 rounded-lg py-2 min-h-[36px]"
                        >
                          Remove
                        </button>
                      </div>
                    </div>
                  ) : (
                    <label
                      onDragOver={(e) => { e.preventDefault(); if (!dragOver) setDragOver(true); }}
                      onDragLeave={() => setDragOver(false)}
                      onDrop={(e) => {
                        e.preventDefault();
                        setDragOver(false);
                        const f = e.dataTransfer?.files?.[0];
                        stageFacadeFile(f);
                      }}
                      className={`flex flex-col items-center justify-center gap-1 w-full py-4 border-2 border-dashed rounded-xl text-sm cursor-pointer transition-colors min-h-[80px] ${
                        dragOver
                          ? "border-forest-500 bg-forest-50 text-forest-700"
                          : "border-stone-300 text-stone-500 hover:border-forest-400 hover:text-forest-600"
                      }`}
                      data-testid="facade-pending-upload-btn"
                    >
                      <Upload className="w-4 h-4" />
                      <span>{dragOver ? "Drop image here" : "Upload facade"}</span>
                      <span className="text-[10px] text-stone-400">{dragOver ? " " : "Click or drag · attached after Create"}</span>
                      <input type="file" accept="image/*" className="hidden" onChange={(e) => stageFacadeFile(e.target.files?.[0])} />
                    </label>
                  )
                ) : facadeUrl ? (
                  <div
                    className="relative group"
                    onDragOver={(e) => { if (draggingEvidenceId) { e.preventDefault(); setFacadeDropActive(true); } }}
                    onDragLeave={() => setFacadeDropActive(false)}
                    onDrop={(e) => {
                      e.preventDefault();
                      setFacadeDropActive(false);
                      const id = e.dataTransfer?.getData("text/plain") || draggingEvidenceId;
                      if (id) handleAssignFacadeFromEvidence(id);
                    }}
                  >
                    <img src={facadeUrl} alt="Facade" className={`w-full h-32 object-cover rounded-lg transition-all ${facadeDropActive ? "ring-4 ring-forest-500 ring-offset-2" : ""}`} data-testid="facade-preview" />
                    <button
                      type="button"
                      onClick={handleRemoveFacade}
                      title="Remove facade"
                      data-testid="facade-remove-overlay-btn"
                      className="absolute top-2 right-2 w-8 h-8 bg-white/90 hover:bg-red-50 text-red-600 hover:text-red-700 rounded-full shadow-md flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                ) : (
                  <label
                    onDragOver={(e) => {
                      // Accept BOTH file drops (from desktop) and evidence-photo drags (intra-form)
                      e.preventDefault();
                      if (draggingEvidenceId || e.dataTransfer?.types?.includes("Files")) {
                        setFacadeDropActive(true);
                      }
                    }}
                    onDragLeave={() => setFacadeDropActive(false)}
                    onDrop={(e) => {
                      e.preventDefault();
                      setFacadeDropActive(false);
                      const id = e.dataTransfer?.getData("text/plain");
                      if (id) {
                        handleAssignFacadeFromEvidence(id);
                        return;
                      }
                      const f = e.dataTransfer?.files?.[0];
                      if (f) handleUploadFacade(f);
                    }}
                    className={`flex flex-col items-center justify-center gap-1 w-full py-4 border-2 border-dashed rounded-xl text-sm cursor-pointer transition-all min-h-[80px] ${
                      facadeDropActive
                        ? "border-forest-500 bg-forest-50 text-forest-700 ring-2 ring-forest-300"
                        : "border-stone-300 text-stone-500 hover:border-forest-400 hover:text-forest-600"
                    }`}
                    data-testid="facade-upload-btn"
                  >
                    <Upload className="w-4 h-4" />
                    <span>{facadeDropActive ? "Drop to set as facade" : "Upload facade"}</span>
                    <span className="text-[10px] text-stone-400">{facadeDropActive ? " " : "Click, or drag a photo from Evidence below"}</span>
                    <input type="file" accept="image/*" className="hidden" onChange={e => e.target.files?.[0] && handleUploadFacade(e.target.files[0])} />
                  </label>
                )}
                {facadeUrl && (
                  <label className="block text-center text-xs text-forest-700 hover:text-forest-800 font-semibold cursor-pointer min-h-[36px] py-2" data-testid="facade-replace-btn">
                    Replace photo
                    <input type="file" accept="image/*" className="hidden" onChange={e => e.target.files?.[0] && handleUploadFacade(e.target.files[0])} />
                  </label>
                )}
              </div>
            </div>
            {/* Completed badge */}
            {editingOrder?.status === "completed" && (
              <div className="bg-amber-50 border border-amber-200 rounded-lg px-4 py-2 text-sm text-amber-800 flex items-center gap-2">
                <AlertTriangle className="w-4 h-4" /> This job is completed. Changes will be logged.
              </div>
            )}

            {/* Photos / Evidence */}
            {editingOrder && (
              <div className="bg-stone-50 rounded-xl border border-stone-200 p-4 space-y-3" data-testid="so-photos-section">
                <p className="text-sm font-semibold text-stone-700 flex items-center gap-2"><Camera className="w-4 h-4 text-forest-600" /> Evidence Photos ({editingOrder.photos?.length || 0})</p>
                {editingOrder.photos?.length > 0 && (
                  <div className="flex gap-2 flex-wrap">
                    {editingOrder.photos.map((p, idx) => (
                      <div
                        key={p.id}
                        data-testid={`evidence-thumb-${p.id}`}
                        draggable={!!photoUrls[p.id]}
                        onDragStart={(e) => {
                          if (!photoUrls[p.id]) return;
                          e.dataTransfer.setData("text/plain", p.id);
                          e.dataTransfer.effectAllowed = "copy";
                          setDraggingEvidenceId(p.id);
                        }}
                        onDragEnd={() => setDraggingEvidenceId(null)}
                        onClick={() => { if (photoUrls[p.id]) { setLightboxIndex(idx); setLightboxZoom(1); } }}
                        title="Click to view · drag to Facade Photo to assign"
                        className={`w-16 h-16 rounded-lg overflow-hidden bg-stone-200 cursor-pointer hover:ring-2 hover:ring-forest-400 transition-all ${draggingEvidenceId === p.id ? "opacity-50 ring-2 ring-forest-500" : ""}`}
                      >
                        {photoUrls[p.id] ? <img src={photoUrls[p.id]} alt="" className="w-full h-full object-cover pointer-events-none" /> : <div className="w-full h-full flex items-center justify-center text-stone-400"><Camera className="w-5 h-5" /></div>}
                      </div>
                    ))}
                  </div>
                )}
                <label className="flex items-center justify-center gap-2 w-full py-2.5 border-2 border-dashed border-stone-300 rounded-xl text-sm text-stone-500 hover:border-forest-400 hover:text-forest-600 cursor-pointer transition-colors" data-testid="upload-evidence-btn">
                  <Upload className="w-4 h-4" /> Upload Photos
                  <input type="file" accept="image/*" multiple className="hidden" onChange={e => handleUploadEvidence(e.target.files)} />
                </label>
                <EvidenceLightbox
                  open={lightboxIndex !== null}
                  photos={editingOrder.photos || []}
                  photoUrls={photoUrls}
                  index={lightboxIndex ?? 0}
                  zoom={lightboxZoom}
                  onIndex={(i) => { setLightboxIndex(i); setLightboxZoom(1); }}
                  onZoom={setLightboxZoom}
                  onClose={() => setLightboxIndex(null)}
                  onAssignFacade={handleAssignFacadeFromEvidence}
                />
              </div>
            )}

            {/* Job Expenses */}
            {editingOrder && (
              <div className="bg-stone-50 rounded-xl border border-stone-200 p-4 space-y-3" data-testid="job-expenses-section">
                <div className="flex items-center justify-between">
                  <p className="text-sm font-semibold text-stone-700 flex items-center gap-2"><DollarSign className="w-4 h-4 text-forest-600" /> Job Expenses</p>
                  {editingOrder.job_expenses_total > 0 && (
                    <span className="text-sm font-bold text-red-600">Total: {formatUSD(editingOrder.job_expenses_total)}</span>
                  )}
                </div>
                {/* Existing expenses */}
                {loadingExpenses ? <p className="text-xs text-stone-400">Loading...</p> : jobExpenses.length > 0 && (
                  <div className="space-y-2">
                    {jobExpenses.map(exp => (
                      <div key={exp.id} className="flex items-center gap-3 bg-white rounded-lg border border-stone-200 px-3 py-2 text-sm">
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-stone-800 truncate">{exp.description?.replace(/^\[SO:.*?\]\s*/, "")}</p>
                          <p className="text-xs text-stone-500">{exp.vendor && `${exp.vendor} · `}{exp.payment_method?.replace("_", " ") || ""}</p>
                        </div>
                        <span className="font-bold text-stone-800">{formatUSD(exp.amount || 0)}</span>
                        {exp.receipt_path ? (
                          <span className="text-xs text-forest-600 flex items-center gap-0.5"><Receipt className="w-3 h-3" /> Receipt</span>
                        ) : (
                          <label className="text-xs text-stone-400 hover:text-forest-600 cursor-pointer flex items-center gap-0.5">
                            <Upload className="w-3 h-3" /> Upload
                            <input type="file" accept="image/*,.pdf" className="hidden" onChange={e => { if (e.target.files[0]) handleUploadJobExpReceipt(exp.id, e.target.files[0]); }} />
                          </label>
                        )}
                      </div>
                    ))}
                  </div>
                )}
                {/* Add new expense */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2 pt-1">
                  <input value={newJobExp.description} onChange={e => setNewJobExp(p => ({ ...p, description: e.target.value }))} placeholder="Description *" className="px-2.5 py-1.5 border border-stone-300 rounded-lg text-sm" data-testid="job-exp-desc" />
                  <CurrencyInput value={newJobExp.amount || 0} onChange={(v) => setNewJobExp(p => ({ ...p, amount: v }))} className="px-2.5 py-1.5 border border-stone-300 rounded-lg text-sm" data-testid="job-exp-amount" />
                  <input value={newJobExp.vendor} onChange={e => setNewJobExp(p => ({ ...p, vendor: e.target.value }))} placeholder="Vendor" className="px-2.5 py-1.5 border border-stone-300 rounded-lg text-sm" />
                  <button type="button" onClick={handleAddJobExpense} data-testid="add-job-exp-btn" className="flex items-center justify-center gap-1 bg-forest-600 hover:bg-forest-700 text-white px-3 py-1.5 rounded-lg text-sm font-medium transition-colors">
                    <Plus className="w-3.5 h-3.5" /> Add
                  </button>
                </div>
                <p className="text-[10px] text-stone-400">Job expenses auto-sync to Accounts Payable.</p>
              </div>
            )}

            <div className="flex gap-3">
              <button type="submit" data-testid="so-submit" className="flex items-center gap-2 bg-forest-600 hover:bg-forest-700 text-white px-5 py-2 rounded-lg text-sm font-medium">
                <Check className="w-4 h-4" /> {editingOrder ? (editingOrder.status === "completed" ? "Update & Sync" : "Update") : form.recurrence !== "one_time" ? "Create Recurring" : "Create"}
              </button>
              <button type="button" onClick={resetForm} className="px-5 py-2 rounded-lg text-sm border border-stone-300 text-stone-600 hover:bg-stone-50">Cancel</button>
            </div>
          </form>
        </div>
      )}

      {/* Week Navigation + view toggles (Calendário/Análise + Mostrar ausentes) */}
      <div className="flex items-center justify-between flex-wrap gap-2">
        <button onClick={() => navigateWeek(-1)} className="p-2 rounded-lg hover:bg-stone-100"><ChevronLeft className="w-5 h-5" /></button>
        <h3 className="font-semibold text-stone-800">{wsLabel} — {weLabel}</h3>
        <button onClick={() => navigateWeek(1)} className="p-2 rounded-lg hover:bg-stone-100"><ChevronRight className="w-5 h-5" /></button>
      </div>
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="inline-flex rounded-lg border border-stone-200 bg-stone-50 p-0.5" data-testid="view-mode-toggle">
          <button type="button" data-testid="view-mode-calendar"
                  onClick={() => setViewMode("calendar")}
                  className={`px-3 py-1.5 text-xs font-semibold rounded-md transition-colors ${
                    viewMode === "calendar" ? "bg-white text-forest-700 shadow-sm" : "text-stone-500 hover:text-stone-700"
                  }`}>Calendário</button>
          <button type="button" data-testid="view-mode-analysis"
                  onClick={() => setViewMode("analysis")}
                  className={`px-3 py-1.5 text-xs font-semibold rounded-md transition-colors ${
                    viewMode === "analysis" ? "bg-white text-forest-700 shadow-sm" : "text-stone-500 hover:text-stone-700"
                  }`}>Análise</button>
          {/* Billing legend — only for financial users. Fase 3 · Part 3. */}
          {canSeeFinancial && (
            <div className="relative flex items-center">
              <button type="button" data-testid="billing-legend-btn"
                      onClick={() => setShowBillingLegend(v => !v)}
                      title="Cores dos cards (borda)"
                      className="ml-1 p-1 rounded text-stone-400 hover:text-stone-700"
              >
                <Info className="w-3.5 h-3.5" />
              </button>
              {showBillingLegend && (
                <div data-testid="billing-legend-panel"
                     className="absolute z-30 top-full left-0 mt-1 w-56 rounded-lg border border-stone-200 bg-white shadow-lg p-3 text-[11px]">
                  <p className="font-semibold text-stone-700 mb-1.5">Bordas do card (concluído)</p>
                  <div className="space-y-1">
                    <div className="flex items-center gap-2"><span className="inline-block w-3 h-3 rounded border-2 border-red-300 bg-red-50" /> Sem fatura ou rascunho</div>
                    <div className="flex items-center gap-2"><span className="inline-block w-3 h-3 rounded border-2 border-amber-300 bg-amber-50" /> Aguardando pagamento</div>
                    <div className="flex items-center gap-2"><span className="inline-block w-3 h-3 rounded border-2 border-green-400 bg-green-50" /> Pago</div>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
        <button type="button" data-testid="toggle-missing"
                onClick={() => setShowMissing(v => !v)}
                className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold border transition-colors ${
                  showMissing ? "border-amber-400 bg-amber-50 text-amber-700" : "border-stone-200 bg-white text-stone-500 hover:bg-stone-50"
                }`}>
          <AlertTriangle className="w-3.5 h-3.5" /> Mostrar ausentes
          {showMissing && missingClients.length > 0 && (
            <span className="ml-1 px-1.5 py-0.5 rounded-full bg-amber-500 text-white text-[10px] leading-none">{missingClients.length}</span>
          )}
        </button>
      </div>

      {/* Missing clients banner (Part C) — read-only, opt-in */}
      {showMissing && (
        <div className="rounded-xl border border-amber-300 bg-amber-50/50 p-3" data-testid="missing-clients-banner">
          <div className="flex items-center justify-between mb-2">
            <p className="text-sm font-semibold text-amber-800 flex items-center gap-1.5">
              <AlertTriangle className="w-4 h-4" /> Clientes recorrentes em atraso
            </p>
            <span className="text-[11px] text-amber-700">{missingLoading ? "buscando..." : `${missingClients.length} cliente${missingClients.length === 1 ? "" : "s"}`}</span>
          </div>
          {missingClients.length === 0 && !missingLoading ? (
            <p className="text-xs text-amber-700/70 py-2">Nenhum cliente em atraso nesta semana.</p>
          ) : (
            <div className="space-y-1.5">
              {missingClients.map(mc => (
                <div key={mc.client_id} data-testid={`missing-client-${mc.client_id}`}
                     className="flex items-center justify-between gap-3 rounded-lg bg-white border border-amber-100 px-3 py-2 text-xs">
                  <div className="min-w-0 flex-1">
                    <p className="font-semibold text-stone-800 truncate">{mc.client_name}</p>
                    <p className="text-[11px] text-stone-500">
                      {mc.city && <span>{mc.city} · </span>}
                      {{"weekly":"Semanal","biweekly":"Quinzenal","monthly":"Mensal"}[mc.recurrence] || mc.recurrence}
                      {" · último em "}{mc.last_recurring_date}
                    </p>
                  </div>
                  <span className="px-2 py-0.5 rounded-full bg-amber-100 text-amber-800 font-semibold whitespace-nowrap">
                    +{mc.days_overdue}d atrasado
                  </span>
                  {!readOnly && (
                    <button type="button" data-testid={`missing-schedule-${mc.client_id}`}
                            onClick={() => handleScheduleMissing(mc)}
                            className="px-3 py-1 rounded-md bg-forest-600 text-white font-semibold hover:bg-forest-700">
                      Agendar
                    </button>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Schedule Tracks — stackable weekly calendars (replaces the old
          single global calendar + crew filter). Each track is its own
          filter/scope; new "Nova agenda" button below the last block. */}
      {viewMode === "calendar" && (
      <div className="space-y-4" data-testid="schedule-tracks-stack">
        {tracksLoading ? (
          <div className="py-8 text-center text-stone-400 text-sm">Loading tracks…</div>
        ) : tracks.length === 0 ? null : tracks.map(track => {
          const trackOrders = orders.filter(o => orderMatchesTrack(o, track));
          return (
            <div key={track.id} data-testid={`track-block-${track.id}`}
                 className={`rounded-2xl border shadow-sm overflow-hidden transition-shadow ${
                   track.is_pinned ? "border-amber-300 bg-white ring-1 ring-amber-200/50" : "border-stone-200 bg-white"
                 } ${draggingTrack?.id === track.id ? "opacity-60" : ""}`}
                 onDragOver={handleTrackDragOver}
                 onDrop={(e) => draggingTrack && handleTrackDrop(e, track)}>
              {/* Track header */}
              <div className={`flex items-center gap-3 px-4 py-3 border-b ${
                track.is_pinned ? "bg-amber-50/60 border-amber-200" : "bg-stone-50/60 border-stone-100"
              }`}>
                <button type="button" data-testid={`track-pin-${track.id}`}
                        onClick={() => togglePin(track)}
                        title={track.is_pinned ? "Desafixar" : "Fixar no topo"}
                        className={`p-1 rounded transition-colors ${
                          track.is_pinned ? "text-amber-600" : "text-stone-400 hover:text-stone-600"
                        }`}>
                  <Pin className={`w-4 h-4 ${track.is_pinned ? "fill-current" : ""}`} />
                </button>
                <div draggable={!readOnly}
                     data-testid={`track-drag-${track.id}`}
                     onDragStart={(e) => handleTrackDragStart(e, track)}
                     className="cursor-grab active:cursor-grabbing p-0.5"
                     title="Arraste para reordenar">
                  <GripVertical className="w-4 h-4 text-stone-300 hover:text-stone-500" />
                </div>
                {track.assignee_type === "team"
                  ? <Truck className="w-4 h-4 text-forest-600 flex-shrink-0" />
                  : <User className="w-4 h-4 text-forest-600 flex-shrink-0" />}
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-stone-800 text-sm truncate">{track.label}</p>
                  <p className="text-[11px] text-stone-500">
                    {track.assignee_type === "team" ? "Equipe" : "Funcionário"}
                    {track.is_default && " · safety-net"}
                    {trackOrders.length > 0 && ` · ${trackOrders.length} ticket${trackOrders.length === 1 ? "" : "s"} nesta semana`}
                  </p>
                </div>
                {!track.is_default && !readOnly && (
                  <button type="button" data-testid={`track-delete-${track.id}`}
                          onClick={() => handleDeleteTrack(track)}
                          title="Excluir agenda"
                          className="p-1 rounded text-stone-400 hover:text-red-500 transition-colors">
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                )}
                {!readOnly && (
                  <button type="button" data-testid={`track-organize-${track.id}`}
                          onClick={() => handleOrganizeWeek(track)}
                          disabled={organizingTrackId === track.id}
                          title="Organizar por cidade — reordena os horários da semana"
                          className={`inline-flex items-center gap-1 px-2 py-1 rounded-md text-[11px] font-semibold border transition-colors ${
                            organizingTrackId === track.id
                              ? "border-stone-200 bg-stone-100 text-stone-400 cursor-wait"
                              : "border-forest-300 text-forest-700 bg-forest-50/50 hover:bg-forest-100"
                          }`}>
                    <MapPin className="w-3 h-3" />
                    {organizingTrackId === track.id ? "Organizando…" : "Organizar"}
                  </button>
                )}
              </div>
              {/* 7-day grid — REUSES the exact same card rendering as before */}
              <div className="p-2 grid grid-cols-7 gap-2" data-testid={`track-week-grid-${track.id}`}>
                {weekDays.map(day => {
                  const dayStr = formatDate(day);
                  const cellKey = `${track.id}::${dayStr}`;
                  const dayOrders = trackOrders
                    .filter(o => o.scheduled_date === dayStr)
                    .sort(routeSort);
                  const isToday = dayStr === formatDate(new Date());
                  const isDragOver = dragOverDay === cellKey;
                  return (
                    <div key={dayStr} data-testid={`track-day-${track.id}-${dayStr}`}
                      onDragOver={(e) => handleDragOver(e, cellKey)}
                      onDragLeave={handleDragLeave}
                      onDrop={(e) => handleDrop(e, dayStr, track)}
                      className={`rounded-xl border min-h-[140px] transition-all duration-150 ${
                        isDragOver
                          ? "border-forest-500 bg-forest-50/60 ring-2 ring-forest-300/50 scale-[1.02]"
                          : isToday
                            ? "border-forest-400 bg-forest-50/30"
                            : "border-stone-200 bg-white"
                      }`}>
                      <div className={`px-2 py-2 text-center border-b ${isDragOver ? "border-forest-300 bg-forest-100/60" : isToday ? "border-forest-200 bg-forest-100/50" : "border-stone-100"}`}>
                        <div className="text-xs text-stone-500">{day.toLocaleDateString("en-US", { weekday: "short" })}</div>
                        <div className={`text-lg font-bold ${isDragOver ? "text-forest-600" : isToday ? "text-forest-700" : "text-stone-700"}`}>{day.getDate()}</div>
                      </div>
                      <div className="p-1.5 space-y-1">
                        {dayOrders.map((order, stopIdx) => {
                          const canDrag = !readOnly && order.status !== "completed" && order.status !== "cancelled";
                          const isFirstStop = stopIdx === 0 && order.status !== "completed" && order.status !== "cancelled";
                          const isDropTarget = dragOverOrderId === order.id && draggingOrder && draggingOrder.id !== order.id;
                          return (
                          <div key={order.id} data-testid={`order-card-${order.id}`}
                            draggable={canDrag}
                            onDragStart={(e) => handleDragStart(e, order)}
                            onDragEnd={handleDragEnd}
                            onDragOver={canDrag ? (e) => handleCardDragOver(e, order.id) : undefined}
                            onDrop={canDrag ? (e) => handleCardDrop(e, order) : undefined}
                            className={`relative rounded-lg border p-2 pl-3 text-xs transition-all group ${
                              order.status === "completed" ? "pb-3" : ""
                            } ${
                              canDrag ? "cursor-grab active:cursor-grabbing" : "cursor-default"
                            } ${
                              draggingOrder?.id === order.id
                                ? "opacity-40 scale-95 border-dashed border-forest-400 bg-forest-50"
                                : isDropTarget
                                  ? "border-forest-500 bg-forest-50 ring-2 ring-forest-400/40"
                                  // Fase 3 · Part 3 — billing-status coloured borders
                                  // (only present when the server exposed billing_status
                                  // to this user, i.e. financial view perm). Falls back
                                  // to the pre-Fase-3 styling when field is absent.
                                  : canSeeFinancial && (order.billing_status === "not_created" || order.billing_status === "draft")
                                    ? "bg-red-50 border-2 border-red-300 hover:shadow-sm"
                                    : canSeeFinancial && order.billing_status === "awaiting"
                                      ? "bg-amber-50 border-2 border-amber-300 hover:shadow-sm"
                                      : canSeeFinancial && order.billing_status === "paid"
                                        ? "bg-green-50 border-2 border-green-400 hover:shadow-sm"
                                        : isFirstStop
                                          ? "bg-white border-l-[3px] border-forest-500 border-t border-r border-b border-stone-200 hover:shadow-sm"
                                          : order.status === "completed"
                                            ? "bg-green-50 border-green-200 hover:shadow-sm"
                                            : "bg-white border-stone-200 hover:shadow-sm"
                            }`}
                            onClick={() => !readOnly && handleEdit(order)}>
                            <span
                              data-testid={`route-badge-${order.id}`}
                              className={`absolute -top-1.5 -left-1.5 z-10 min-w-[18px] h-[18px] px-1 flex items-center justify-center rounded-full text-[10px] font-bold shadow ${
                                isFirstStop
                                  ? "bg-forest-600 text-white ring-2 ring-white"
                                  : order.status === "completed"
                                    ? "bg-green-500 text-white"
                                    : "bg-stone-700 text-white"
                              }`}
                              title={isFirstStop ? "Route Start" : `Stop #${stopIdx + 1}`}
                            >
                              {stopIdx + 1}
                            </span>
                            <div className="font-medium text-stone-800 truncate flex items-center gap-1">
                              {order.status === "completed" && <CheckCircle className="w-3 h-3 text-green-600 flex-shrink-0" />}
                              {order.client_name}
                            </div>
                            {order.start_time && (
                              <div className="flex items-center gap-1 text-stone-400 mt-0.5">
                                <Clock className="w-3 h-3" />
                                {formatTime12h(order.start_time)}{order.end_time && ` - ${formatTime12h(order.end_time)}`}
                              </div>
                            )}
                            {order.recurrence && order.recurrence !== "one_time" && (
                              <div className="flex items-center gap-1 text-forest-500 mt-0.5">
                                <RefreshCw className="w-2.5 h-2.5" />
                                <span className="text-[9px] font-medium capitalize">{order.recurrence}</span>
                              </div>
                            )}
                            {order.confirmation_status && order.status !== "completed" && order.status !== "cancelled" && (
                              <div className="flex items-center gap-1 mt-0.5" title={`Client: ${order.confirmation_status}`}>
                                {order.confirmation_status === "waiting" && <Clock className="w-2.5 h-2.5 text-amber-500" />}
                                {order.confirmation_status === "confirmed" && <MailCheck className="w-2.5 h-2.5 text-green-600" />}
                                {order.confirmation_status === "declined" && <MailX className="w-2.5 h-2.5 text-red-500" />}
                                <span className={`text-[9px] font-medium ${
                                  order.confirmation_status === "waiting" ? "text-amber-600" :
                                  order.confirmation_status === "confirmed" ? "text-green-700" :
                                  "text-red-600"
                                }`}>
                                  {order.confirmation_status === "waiting" ? "Waiting" : order.confirmation_status === "confirmed" ? "Confirmed" : "Declined"}
                                </span>
                              </div>
                            )}
                            <div className="flex items-center justify-between mt-1">
                              <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${STATUS_COLORS[order.status] || STATUS_COLORS.scheduled}`}>
                                {order.status}
                              </span>
                              {order.status === "in_progress" && order.actual_start_time && (
                                <span
                                  data-testid={`order-elapsed-${order.id}`}
                                  className="text-[10px] font-mono font-semibold text-amber-700 tabular-nums"
                                  title={`Started at ${new Date(order.actual_start_time).toLocaleTimeString()}`}
                                >
                                  {formatElapsed(order.actual_start_time)}
                                </span>
                              )}
                              {!readOnly && order.status !== "completed" && order.status !== "cancelled" && (
                                <div className="opacity-0 group-hover:opacity-100 flex gap-0.5 transition-opacity">
                                  <button onClick={(e) => { e.stopPropagation(); openCompleteModal(order); }} title="Complete Job" className="p-0.5 rounded text-green-600 hover:bg-green-50">
                                    <Check className="w-3 h-3" />
                                  </button>
                                  <button onClick={(e) => { e.stopPropagation(); handleDelete(order); }} title="Delete" className="p-0.5 rounded text-red-500 hover:bg-red-50">
                                    <Trash2 className="w-3 h-3" />
                                  </button>
                                </div>
                              )}
                              {/* Fase 3 · Part 5 — "Confirmar pagamento" quick action.
                                  Visible only to financial users on completed tickets
                                  whose invoice hasn't been paid yet (or doesn't exist). */}
                              {!readOnly && canSeeFinancial && order.status === "completed" && order.billing_status && order.billing_status !== "paid" && (
                                <button
                                  data-testid={`confirm-payment-btn-${order.id}`}
                                  onClick={(e) => { e.stopPropagation(); setPaymentModalOrder(order); }}
                                  title="Confirmar pagamento (Cash/Zelle/CashApp/Other)"
                                  className="p-0.5 rounded text-green-700 bg-white/80 hover:bg-green-100 border border-green-300"
                                >
                                  <DollarSign className="w-3 h-3" />
                                </button>
                              )}
                            </div>
                            {(() => {
                              const bar = getEfficiencyBar(order);
                              if (!bar) return null;
                              return (
                                <div
                                  className="absolute left-1 right-1 bottom-1 h-[5px] rounded-full bg-stone-200/60 overflow-hidden"
                                  data-testid={`efficiency-track-${order.id}`}
                                  title={bar.label}
                                >
                                  <div
                                    data-testid={`efficiency-bar-${order.id}`}
                                    data-severity={bar.severity}
                                    className={`h-full ${bar.color} rounded-full transition-all duration-300`}
                                    style={{ width: `${bar.widthPct}%` }}
                                  />
                                </div>
                              );
                            })()}
                          </div>
                          );
                        })}
                        {!readOnly && dayOrders.length === 0 && (
                          <button onClick={() => openNewForm(dayStr, track.id)}
                            data-testid={`track-day-add-${track.id}-${dayStr}`}
                            className="w-full py-3 text-stone-300 hover:text-forest-500 flex items-center justify-center gap-1 transition-colors rounded-lg hover:bg-forest-50/50 text-xs font-medium">
                            <Plus className="w-4 h-4" />
                            {nextSlots[`${track.id}::${dayStr}`] && (
                              <span className="tabular-nums" data-testid={`next-slot-${track.id}-${dayStr}`}>
                                {formatTime12h(nextSlots[`${track.id}::${dayStr}`])}
                              </span>
                            )}
                          </button>
                        )}
                        {!readOnly && dayOrders.length > 0 && (
                          <button onClick={() => openNewForm(dayStr, track.id)}
                            data-testid={`track-day-add-more-${track.id}-${dayStr}`}
                            className="w-full py-1.5 text-stone-400 hover:text-forest-500 flex items-center justify-center gap-1 transition-colors rounded text-[10px] font-medium">
                            <Plus className="w-3 h-3" />
                            {nextSlots[`${track.id}::${dayStr}`] && (
                              <span className="tabular-nums" data-testid={`next-slot-${track.id}-${dayStr}`}>
                                {formatTime12h(nextSlots[`${track.id}::${dayStr}`])}
                              </span>
                            )}
                          </button>
                        )}
                      </div>
                      {/* Part A · daily completed-value total — only rendered
                          when the backend included it (i.e. user has financial
                          view perm). Guarded by canSeeFinancial for safety. */}
                      {canSeeFinancial && track.day_completed_totals && (
                        <div
                          data-testid={`day-completed-total-${track.id}-${dayStr}`}
                          className="px-2 py-1 border-t border-stone-100 text-right text-[11px] text-stone-500 tabular-nums"
                        >
                          {(track.day_completed_totals[dayStr] || 0) > 0
                            ? `$${(track.day_completed_totals[dayStr] || 0).toFixed(2)}`
                            : ""}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
        {!readOnly && !tracksLoading && (
          <button
            type="button"
            data-testid="add-track-btn"
            onClick={() => setShowNewTrackModal(true)}
            className="w-full py-3 rounded-xl border border-dashed border-stone-300 text-stone-400 hover:border-forest-400 hover:text-forest-600 flex items-center justify-center gap-2 text-sm font-medium transition-colors"
          >
            <Plus className="w-4 h-4" /> Adicionar agenda
          </button>
        )}
      </div>
      )}

      {/* Analysis view (Part B) — read-only insights over the visible week.
          Reuses the same card/pill styling as the Calendário view. */}
      {viewMode === "analysis" && (
        <div className="rounded-2xl border border-stone-200 bg-white shadow-sm p-4" data-testid="analysis-view">
          <div className="flex items-center gap-2 flex-wrap mb-4">
            {[
              { k: "city", label: "Por cidade" },
              { k: "recurrence", label: "Por recorrência" },
              { k: "value", label: "Por valor" },
            ].map(opt => (
              <button
                key={opt.k}
                type="button"
                data-testid={`analysis-group-${opt.k}`}
                onClick={() => setAnalysisGroupBy(opt.k)}
                className={`px-3 py-1.5 text-xs font-semibold rounded-full border transition-colors ${
                  analysisGroupBy === opt.k
                    ? "bg-forest-600 border-forest-600 text-white"
                    : "bg-white border-stone-300 text-stone-600 hover:bg-stone-100"
                }`}
              >
                {opt.label}
              </button>
            ))}
          </div>
          {analysisLoading ? (
            <div className="py-8 text-center text-stone-400 text-sm">Carregando análise…</div>
          ) : !analysisData ? null : (analysisGroupBy === "value" && Array.isArray(analysisData.items)) ? (
            <div className="space-y-1.5" data-testid="analysis-value-list">
              {analysisData.items.length === 0 ? (
                <p className="py-6 text-center text-stone-400 text-sm">Nenhum ticket nesta semana.</p>
              ) : analysisData.items.map(it => (
                <div key={it.id} className="flex items-center justify-between gap-3 rounded-lg border border-stone-200 px-3 py-2 text-sm">
                  <div className="min-w-0 flex-1">
                    <p className="font-medium text-stone-800 truncate">{it.client_name}</p>
                    <p className="text-[11px] text-stone-500">
                      {it.city && <span>{it.city} · </span>}
                      {it.scheduled_date}
                    </p>
                  </div>
                  {canSeeFinancial && "total_amount" in it && (
                    <span className="font-semibold text-stone-800 tabular-nums whitespace-nowrap">
                      ${it.total_amount.toFixed(2)}
                    </span>
                  )}
                </div>
              ))}
            </div>
          ) : Array.isArray(analysisData.groups) ? (
            <div className="space-y-2" data-testid="analysis-groups-list">
              {analysisData.groups.length === 0 ? (
                <p className="py-6 text-center text-stone-400 text-sm">Nenhum ticket nesta semana.</p>
              ) : analysisData.groups.map((g, gi) => (
                <details key={`${g.label}-${gi}`} data-testid={`analysis-group-item-${gi}`}
                         className="group rounded-xl border border-stone-200 overflow-hidden">
                  <summary className="cursor-pointer list-none px-4 py-3 flex items-center justify-between gap-3 hover:bg-stone-50 transition-colors">
                    <div className="min-w-0 flex-1">
                      <p className="font-semibold text-stone-800">{g.label}</p>
                      <p className="text-[11px] text-stone-500">{g.count} ticket{g.count === 1 ? "" : "s"}</p>
                    </div>
                    {canSeeFinancial && "total_amount" in g && (
                      <span className="font-semibold text-stone-800 tabular-nums whitespace-nowrap">
                        ${g.total_amount.toFixed(2)}
                      </span>
                    )}
                    <span className="text-stone-400 text-xs group-open:rotate-90 transition-transform">›</span>
                  </summary>
                  <div className="border-t border-stone-100 divide-y divide-stone-100 bg-stone-50/40">
                    {g.clients.map(c => (
                      <div key={c.id} className="px-4 py-2 flex items-center justify-between gap-3 text-sm">
                        <div className="min-w-0">
                          <p className="text-stone-800 truncate">{c.client_name}</p>
                          <p className="text-[11px] text-stone-500">{c.city} · {c.scheduled_date}</p>
                        </div>
                        {canSeeFinancial && "total_amount" in c && (
                          <span className="tabular-nums text-stone-600 whitespace-nowrap">${c.total_amount.toFixed(2)}</span>
                        )}
                      </div>
                    ))}
                  </div>
                </details>
              ))}
            </div>
          ) : null}
        </div>
      )}

      {/* ===== Complete Job Modal ===== */}
      {completeModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4" data-testid="complete-job-modal">
          <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full max-h-[90vh] flex flex-col overflow-hidden">
            <div className="px-6 py-4 border-b border-stone-200 bg-stone-50 flex items-center justify-between flex-shrink-0">
              <h3 className="font-semibold text-stone-800 flex items-center gap-2"><CheckCircle className="w-5 h-5 text-green-600" /> Complete Job</h3>
              <button type="button" onClick={() => { setCompleteModal(null); setPhotoUrls({}); }} className="p-1 rounded hover:bg-stone-200 text-stone-400"><X className="w-5 h-5" /></button>
            </div>
            <div className="flex-1 overflow-y-auto p-6 space-y-4">
              <div className="bg-stone-50 rounded-lg p-4 text-sm">
                <p className="font-medium text-stone-800">{completeModal.client_name}</p>
                <p className="text-xs text-stone-500 mt-1">{completeModal.scheduled_date} · {completeModal.services?.map(s => s.description).join(", ")}</p>
                {completeModal.notes && <p className="text-xs text-stone-400 mt-1 italic">{completeModal.notes}</p>}
              </div>

              {/* Photo Upload */}
              <div>
                <p className="text-sm font-medium text-stone-700 mb-2 flex items-center gap-1"><Camera className="w-4 h-4 text-forest-600" /> Job Photos (Before & After)</p>
                {/* Existing photos */}
                {completeModal.photos?.length > 0 && (
                  <div className="grid grid-cols-3 gap-2 mb-3">
                    {completeModal.photos.map(p => (
                      <div key={p.id} className="aspect-square rounded-lg overflow-hidden bg-stone-100 border border-stone-200">
                        {photoUrls[p.id] ? <img src={photoUrls[p.id]} alt={p.original_filename} className="w-full h-full object-cover" />
                          : <div className="w-full h-full flex items-center justify-center"><ImageIcon className="w-6 h-6 text-stone-300" /></div>}
                      </div>
                    ))}
                  </div>
                )}
                <label className="flex items-center justify-center gap-2 w-full py-3 border-2 border-dashed border-stone-300 rounded-xl text-sm text-stone-500 hover:border-forest-400 hover:text-forest-600 cursor-pointer transition-colors" data-testid="upload-job-photos">
                  <Upload className="w-4 h-4" />
                  {uploading ? "Uploading..." : "Upload Photos"}
                  <input type="file" accept="image/*" multiple className="hidden" onChange={(e) => handleUploadJobPhoto(e.target.files)} disabled={uploading} />
                </label>
                {(!completeModal.photos || completeModal.photos.length === 0) && (
                  <p className="text-xs text-amber-600 mt-1 flex items-center gap-1"><AlertTriangle className="w-3 h-3" /> Photos are recommended before completing</p>
                )}
              </div>
            </div>

            <div className="flex items-center gap-3 px-6 py-4 border-t border-stone-200 bg-white flex-shrink-0">
              <button type="button" onClick={() => { setCompleteModal(null); setPhotoUrls({}); }} className="flex-1 px-4 py-2.5 rounded-lg text-sm border border-stone-300 text-stone-600 hover:bg-stone-50 transition-colors">Cancel</button>
              <button type="button" onClick={handleCompleteJob} data-testid="confirm-complete-job-btn"
                className="flex-1 flex items-center justify-center gap-2 bg-green-600 hover:bg-green-700 text-white px-4 py-2.5 rounded-lg text-sm font-medium transition-colors">
                <CheckCircle className="w-4 h-4" /> Complete Job
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ===== Invoice Prompt Modal (after Complete Job) ===== */}
      {invoicePromptModal && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4"
          data-testid="invoice-prompt-modal"
        >
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6 text-center">
            <div className="w-14 h-14 rounded-full bg-forest-50 flex items-center justify-center mx-auto mb-4">
              <Receipt className="w-7 h-7 text-forest-600" />
            </div>
            <h3 className="text-lg font-semibold text-stone-800 mb-1">
              Service Order #{(invoicePromptModal.so.id || "").slice(0, 8).toUpperCase()} completed!
            </h3>
            <p className="text-sm text-stone-500 mb-6">Would you like to generate the invoice now?</p>
            <div className="flex items-center justify-center gap-3 flex-wrap">
              <button
                data-testid="invoice-prompt-skip"
                onClick={() => {
                  const pending = invoicePromptModal.renewPayload;
                  setInvoicePromptModal(null);
                  if (pending) setRenewModal(pending);
                }}
                className="px-5 py-2.5 rounded-lg text-sm font-medium border border-stone-300 text-stone-600 hover:bg-stone-50 transition-colors"
              >
                Skip for Now
              </button>
              <button
                data-testid="invoice-prompt-generate"
                onClick={() => {
                  const so = invoicePromptModal.so;
                  const pending = invoicePromptModal.renewPayload;
                  setInvoicePromptModal(null);
                  if (pending) setRenewModal(pending);
                  if (onRequestInvoice) onRequestInvoice(buildInvoicePrefill(so));
                }}
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-semibold bg-forest-600 hover:bg-forest-700 text-white shadow transition-colors"
              >
                <DollarSign className="w-4 h-4" /> Generate Invoice
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ===== Renew Recurring Modal ===== */}
      {renewModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm" data-testid="renew-modal">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full mx-4 p-6 text-center">
            <div className="w-14 h-14 rounded-full bg-forest-50 flex items-center justify-center mx-auto mb-4">
              <RefreshCw className="w-7 h-7 text-forest-600" />
            </div>
            <h3 className="text-lg font-semibold text-stone-800 mb-2">Renew Recurring Schedule?</h3>
            <p className="text-sm text-stone-500 mb-1">All service orders for <strong>{renewModal.client_name}</strong> in this {renewModal.recurrence} cycle have been completed.</p>
            <p className="text-sm text-stone-500 mb-6">Would you like to renew for another <strong>{renewModal.recurrence_months} months</strong>?</p>
            <div className="flex items-center justify-center gap-3">
              <button onClick={() => setRenewModal(null)} data-testid="renew-cancel-btn" className="px-5 py-2 rounded-lg text-sm border border-stone-300 text-stone-600 hover:bg-stone-50 transition-colors">No, thanks</button>
              <button onClick={handleRenew} data-testid="renew-confirm-btn" className="px-5 py-2 rounded-lg text-sm font-medium text-white bg-forest-600 hover:bg-forest-700 transition-colors flex items-center gap-2">
                <RefreshCw className="w-4 h-4" /> Renew
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ===== Confirm Modal ===== */}
      {confirmModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm" data-testid="confirm-modal">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full mx-4 p-6 text-center">
            <div className={`w-14 h-14 rounded-full flex items-center justify-center mx-auto mb-4 ${confirmModal.danger ? "bg-red-50" : "bg-forest-50"}`}>
              {confirmModal.danger ? <AlertTriangle className="w-7 h-7 text-red-500" /> : <Info className="w-7 h-7 text-forest-600" />}
            </div>
            <h3 className="text-lg font-semibold text-stone-800 mb-2">{confirmModal.title}</h3>
            <p className="text-sm text-stone-500 mb-6">{confirmModal.message}</p>
            <div className="flex items-center justify-center gap-3">
              <button type="button" onClick={() => setConfirmModal(null)} data-testid="confirm-cancel-btn" className="px-5 py-2 rounded-lg text-sm border border-stone-300 text-stone-600 hover:bg-stone-50 transition-colors">Cancel</button>
              <button type="button" onClick={confirmModal.onConfirm} data-testid="confirm-ok-btn" className={`px-5 py-2 rounded-lg text-sm font-medium text-white transition-colors ${confirmModal.danger ? "bg-red-600 hover:bg-red-700" : "bg-forest-600 hover:bg-forest-700"}`}>
                Confirm
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ===== Cascade Modal (Recurring Series Update / Delete) ===== */}
      {cascadeModal && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/55 backdrop-blur-sm p-4" data-testid="cascade-modal">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6">
            <div className={`w-14 h-14 rounded-full flex items-center justify-center mx-auto mb-4 ${cascadeModal.mode === "delete" ? "bg-red-50" : "bg-cyan-50"}`}>
              {cascadeModal.mode === "delete"
                ? <Trash2 className="w-7 h-7 text-red-500" />
                : <RefreshCw className="w-7 h-7 text-cyan-600" />}
            </div>
            <h3 className="text-lg font-semibold text-stone-800 text-center mb-2">
              {cascadeModal.mode === "delete" ? "Recurring series detected" : "Recurring series detected"}
            </h3>
            <p className="text-sm text-stone-600 text-center mb-1">
              This ticket is part of a recurring schedule.
            </p>
            <p className="text-sm text-stone-500 text-center mb-6">
              You have <strong className="text-stone-800">{cascadeModal.futureOpenCount}</strong> future open ticket{cascadeModal.futureOpenCount === 1 ? "" : "s"} in this series.{" "}
              {cascadeModal.mode === "delete"
                ? "Delete only this one or the entire future series?"
                : "Apply changes to only this one or to all future tickets?"}
            </p>
            <div className="flex flex-col gap-2">
              <button
                type="button"
                onClick={cascadeModal.onSeries}
                data-testid="cascade-all-future-btn"
                className={`w-full px-5 py-2.5 rounded-lg text-sm font-semibold text-white transition-colors ${cascadeModal.mode === "delete" ? "bg-red-600 hover:bg-red-700" : "bg-forest-600 hover:bg-forest-700"}`}
              >
                {cascadeModal.mode === "delete"
                  ? `Delete all ${cascadeModal.futureOpenCount + 1} future tickets`
                  : `Apply to all ${cascadeModal.futureOpenCount + 1} future tickets`}
              </button>
              <button
                type="button"
                onClick={cascadeModal.onSingle}
                data-testid="cascade-only-this-btn"
                className="w-full px-5 py-2.5 rounded-lg text-sm font-medium border border-stone-300 text-stone-700 hover:bg-stone-50 transition-colors"
              >
                Only this one
              </button>
              <button
                type="button"
                onClick={() => setCascadeModal(null)}
                data-testid="cascade-cancel-btn"
                className="w-full px-5 py-2 rounded-lg text-xs text-stone-500 hover:text-stone-700 transition-colors"
              >
                Cancel
              </button>
            </div>
            <p className="text-[11px] text-stone-400 text-center mt-3">
              Completed and cancelled tickets are never affected.
            </p>
          </div>
        </div>
      )}

      {/* Nova agenda modal (searchable autocomplete for user/team). */}
      {showNewTrackModal && (
        <NewTrackModal
          api={api}
          teams={teams}
          teamMembers={teamMembers}
          onClose={() => setShowNewTrackModal(false)}
          onCreated={() => { setShowNewTrackModal(false); fetchTracks(); }}
        />
      )}
      {/* Fase 3 · Part 5 — Confirmar pagamento (manual/offline). */}
      {paymentModalOrder && (
        <PaymentConfirmModal
          order={paymentModalOrder}
          onClose={() => setPaymentModalOrder(null)}
          onConfirm={handleConfirmPayment}
        />
      )}
    </div>
  );
}

// ====================== NEW TRACK MODAL ======================
// "Nova agenda" — searchable autocomplete for Funcionário or Equipe.
// No native <select>: we filter the already-fetched teams/teamMembers
// client-side by name, case-insensitive, matching anywhere in the name.
function NewTrackModal({ api, teams, teamMembers, onClose, onCreated }) {
  const [assigneeType, setAssigneeType] = useState("user"); // "user" | "team"
  const [query, setQuery] = useState("");
  const [picked, setPicked] = useState(null); // {id, name}
  const [busy, setBusy] = useState(false);
  const source = assigneeType === "team" ? teams : teamMembers;
  const suggestions = query.trim().length === 0
    ? []
    : source
        .filter((x) => {
          const name = (x.name || x.email || "").toLowerCase();
          return name.includes(query.trim().toLowerCase());
        })
        .slice(0, 6);
  const idOf = (x) => (assigneeType === "team" ? x.id : (x._id || x.id));
  const labelOf = (x) => x.name || x.email || "(sem nome)";

  const handleCreate = async () => {
    if (!picked || busy) return;
    setBusy(true);
    try {
      await api.post("/admin/schedule-tracks", {
        assignee_type: assigneeType,
        assignee_id: picked.id,
      });
      toast.success(`Agenda "${picked.name}" criada`);
      onCreated?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Falha ao criar agenda");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4"
         data-testid="new-track-modal"
         onMouseDown={(e) => { if (e.target === e.currentTarget) onClose?.(); }}>
      <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden">
        <div className="px-6 py-4 border-b border-stone-100 flex items-center justify-between">
          <div>
            <h3 className="font-semibold text-stone-800">Nova agenda</h3>
            <p className="text-xs text-stone-500 mt-0.5">Cada agenda vira um calendário semanal próprio.</p>
          </div>
          <button type="button" onClick={onClose} className="p-1 rounded hover:bg-stone-100 text-stone-400"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-6 space-y-5">
          <div className="grid grid-cols-2 gap-3">
            <button type="button" data-testid="new-track-type-user"
              onClick={() => { setAssigneeType("user"); setPicked(null); setQuery(""); }}
              className={`flex flex-col items-center gap-2 py-4 rounded-xl border-2 transition-colors ${
                assigneeType === "user" ? "border-forest-500 bg-forest-50 text-forest-700" : "border-stone-200 text-stone-500 hover:border-stone-300"
              }`}>
              <User className="w-6 h-6" /><span className="text-sm font-semibold">Funcionário</span>
            </button>
            <button type="button" data-testid="new-track-type-team"
              onClick={() => { setAssigneeType("team"); setPicked(null); setQuery(""); }}
              className={`flex flex-col items-center gap-2 py-4 rounded-xl border-2 transition-colors ${
                assigneeType === "team" ? "border-forest-500 bg-forest-50 text-forest-700" : "border-stone-200 text-stone-500 hover:border-stone-300"
              }`}>
              <Truck className="w-6 h-6" /><span className="text-sm font-semibold">Equipe</span>
            </button>
          </div>
          <div className="relative">
            <label className="block text-xs uppercase tracking-wider text-stone-500 mb-1">
              {assigneeType === "team" ? "Equipe" : "Funcionário"}
            </label>
            <input
              data-testid="new-track-search"
              type="text"
              value={picked ? picked.name : query}
              placeholder="Digite o nome..."
              onChange={(e) => { setPicked(null); setQuery(e.target.value); }}
              className="w-full px-3 py-2.5 rounded-lg border border-stone-300 text-sm focus:outline-none focus:ring-2 focus:ring-forest-400/50 focus:border-forest-400"
              autoFocus
            />
            {!picked && query.trim().length > 0 && (
              <div className="absolute z-10 left-0 right-0 mt-1 rounded-lg border border-stone-200 bg-white shadow-lg overflow-hidden"
                   data-testid="new-track-suggestions">
                {suggestions.length === 0 ? (
                  <div className="px-3 py-2 text-sm text-stone-400">Nenhum resultado</div>
                ) : suggestions.map((x) => (
                  <button
                    key={idOf(x)}
                    type="button"
                    data-testid={`new-track-suggest-${idOf(x)}`}
                    onClick={() => { setPicked({ id: idOf(x), name: labelOf(x) }); setQuery(""); }}
                    className="w-full text-left px-3 py-2 text-sm hover:bg-forest-50 flex items-center gap-2"
                  >
                    {assigneeType === "team" ? <Truck className="w-3.5 h-3.5 text-stone-400" /> : <User className="w-3.5 h-3.5 text-stone-400" />}
                    <span>{labelOf(x)}</span>
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
        <div className="px-6 py-4 border-t border-stone-100 flex items-center gap-3 justify-end">
          <button type="button" onClick={onClose}
            className="px-4 py-2 rounded-lg text-sm border border-stone-300 text-stone-600 hover:bg-stone-50 transition-colors">
            Cancelar
          </button>
          <button
            type="button"
            data-testid="new-track-create-btn"
            disabled={!picked || busy}
            onClick={handleCreate}
            className={`px-4 py-2 rounded-lg text-sm font-semibold border-2 transition-colors ${
              picked && !busy ? "border-forest-500 bg-forest-500 text-white hover:bg-forest-600" : "border-stone-200 bg-stone-100 text-stone-400 cursor-not-allowed"
            }`}
          >
            {busy ? "Criando…" : "Criar agenda"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ====================== PAYMENT CONFIRM MODAL ======================
// Fase 3 · Part 5 — "Confirmar pagamento". Radio Cash/Zelle/CashApp/Other
// + optional note. Compact modal — the flow is a single, quick click.
function PaymentConfirmModal({ order, onClose, onConfirm }) {
  const [method, setMethod] = useState("cash");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const methods = [
    { k: "cash", label: "Cash" },
    { k: "zelle", label: "Zelle" },
    { k: "cashapp", label: "CashApp" },
    { k: "other", label: "Other" },
  ];
  const handle = async () => {
    if (busy) return;
    setBusy(true);
    try { await onConfirm(order, method, note); }
    finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4"
         data-testid="payment-confirm-modal"
         onMouseDown={(e) => { if (e.target === e.currentTarget) onClose?.(); }}>
      <div className="bg-white rounded-2xl shadow-2xl max-w-sm w-full overflow-hidden">
        <div className="px-6 py-4 border-b border-stone-100 flex items-center justify-between">
          <div>
            <h3 className="font-semibold text-stone-800">Confirmar pagamento</h3>
            <p className="text-xs text-stone-500 mt-0.5">
              {order.client_name}{order.total_amount ? ` — $${Number(order.total_amount).toFixed(2)}` : ""}
            </p>
          </div>
          <button type="button" onClick={onClose} className="p-1 rounded hover:bg-stone-100 text-stone-400"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-6 space-y-5">
          <div>
            <label className="block text-xs uppercase tracking-wider text-stone-500 mb-2">Forma de pagamento</label>
            <div className="grid grid-cols-2 gap-2">
              {methods.map(m => (
                <button
                  key={m.k}
                  type="button"
                  data-testid={`payment-method-${m.k}`}
                  onClick={() => setMethod(m.k)}
                  className={`py-2.5 rounded-lg text-sm font-semibold border-2 transition-colors ${
                    method === m.k ? "border-forest-500 bg-forest-50 text-forest-700" : "border-stone-200 text-stone-600 hover:border-stone-300"
                  }`}
                >{m.label}</button>
              ))}
            </div>
          </div>
          <div>
            <label className="block text-xs uppercase tracking-wider text-stone-500 mb-1">Nota (opcional)</label>
            <input
              data-testid="payment-note-input"
              type="text"
              value={note}
              placeholder="Ex: ref #A1B2, recibo 1234..."
              onChange={(e) => setNote(e.target.value)}
              className="w-full px-3 py-2 rounded-lg border border-stone-300 text-sm focus:outline-none focus:ring-2 focus:ring-forest-400/50 focus:border-forest-400"
            />
          </div>
        </div>
        <div className="px-6 py-4 border-t border-stone-100 flex items-center gap-3 justify-end">
          <button type="button" onClick={onClose}
            className="px-4 py-2 rounded-lg text-sm border border-stone-300 text-stone-600 hover:bg-stone-50 transition-colors">Cancelar</button>
          <button
            type="button"
            data-testid="payment-confirm-btn"
            disabled={busy}
            onClick={handle}
            className={`px-4 py-2 rounded-lg text-sm font-semibold border-2 transition-colors ${
              busy ? "border-stone-200 bg-stone-100 text-stone-400 cursor-not-allowed" : "border-green-500 bg-green-500 text-white hover:bg-green-600"
            }`}
          >
            {busy ? "Confirmando…" : "Confirmar pagamento"}
          </button>
        </div>
      </div>
    </div>
  );
}

